/**
 * `AW1` · La raíz de la aplicación.
 *
 * Monta Refine con el proveedor de datos que habla contra `admin-web`, el
 * `routerProvider`, y **una ruta por pantalla**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⓪ POR QUÉ AHORA SÍ HAY `routerProvider` — `ADR-0136` ①
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Este archivo decía, textual: *«Sin `routerProvider`, y es una decisión de
 * alcance. […] lo va a necesitar cuando existan las otras cinco áreas de
 * navegación»*. **Medido el 22/8: existen SIETE**, y las siete estaban montadas
 * en la MISMA página, una debajo de la otra. La condición que el archivo se puso
 * a sí mismo para revisar la decisión se había cumplido hacía rato y nadie la
 * miraba. `ADR-0136` la atiende.
 *
 * ⛔ **Lo que este cambio NO hace, y es la mitad de la decisión:** las siete
 * pantallas **no se migran**. Cada una gana una ruta que la monta y nada más.
 * Cero refactor interno, cero cambio a sus componentes, cero movimiento de
 * archivos. El motivo es de riesgo y no de gusto: hay pantallas que HOY pasan sus
 * casos de prueba y ningún caso depende de cómo estén estructuradas por dentro,
 * así que un refactor sólo puede restar. **Que las siete sigan siendo lo que eran
 * es deuda declarada en `ADR-0136` §Consecuencias, no deuda escondida acá.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① ⭐ POR QUÉ `HashRouter` Y NO `BrowserRouter` — ELECCIÓN DE QUIEN IMPLEMENTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0136` §Lo que este ADR NO afirma deja la forma de las rutas abierta. Esto
 * es una elección mía, está en el informe, y se revierte cambiando **un import**.
 *
 * El que decide es `vite.config.ts`, que ya tomó una decisión con la que ésta
 * tiene que convivir: `base: "./"`, *«el MISMO bundle se puede publicar en
 * cualquier bucket sin recompilar»*. Con `BrowserRouter`:
 *
 *   · una URL profunda (`/brazos/hibrido/metricas`) le llega a S3 como una CLAVE que no
 *     existe ⇒ `404`, salvo que alguien configure la distribución de CloudFront
 *     para reescribir todo a `index.html`. **Esa configuración no está en el
 *     repo**, así que el objetivo de `ADR-0136` ② —«una URL sola, pegada en la
 *     barra, deja la vista en el brazo pedido»— fallaría en producción sin que
 *     nada acá se ponga rojo;
 *   · y el `basename` habría que hornearlo en tiempo de build, que es
 *     exactamente lo que `base: "./"` existe para no hacer.
 *
 * `HashRouter` no necesita ninguna de las dos cosas: el fragmento no viaja al
 * servidor, así que `…/index.html#/brazos/hibrido/metricas` anda igual en la raíz de un
 * bucket que en un subdirectorio, sin tocar CloudFront. **El costo es que la URL
 * lleva un `#`**, y es un costo de estética contra un objetivo que sin esto no se
 * cumple.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② ⭐ EL BRAZO VIVE EN LA RUTA — `ADR-0136` ②
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **La forma elegida es el SEGMENTO, `/:brazo/<pantalla>`, y no el parámetro
 * `?brazo=`.** `ADR-0136` §Lo que este ADR NO afirma la deja abierta; ésta es
 * elección de quien implementa y está en el informe. El argumento no es de gusto:
 * con el brazo como segmento, **los enlaces internos son relativos y lo propagan
 * solos**. Con `?brazo=`, cada `<NavLink>` y cada redirección serían un lugar
 * donde olvidarse de reponerlo, y olvidarse no falla: te deja mirando el otro
 * brazo. Revertir a la otra forma es cambiar la tabla de rutas y `Marco`, que son
 * los dos únicos lugares donde el brazo se nombra.
 *
 * **El objetivo que esto cumple**, que es el que `ADR-0136` ② declara: una URL
 * sola —`…/index.html#/brazos/hibrido/metricas`—, pegada en la barra, deja la vista
 * en el brazo pedido **sin ningún clic previo**. Un caso de prueba lo declara en vez
 * de navegarlo.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ ENMIENDA `[17/9/2026]` · `ADR-0082` · **LAS TRES DIRECCIONES DE ARRIBA
 * ESTABAN VENCIDAS, Y ESTE ARCHIVO ES EL PRIMERO QUE LEE QUIEN TOCA RUTEO**
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⛔ Decían ~~`#/hibrido/metricas`~~ —el brazo como PRIMER segmento— en `:37`, `:47`
 * y acá. **Ése es el esquema VIEJO.** El canónico es `#/brazos/<brazo>/<pantalla>`,
 * y el conteo sobre el PRD no deja lugar a duda: **134 ocurrencias del canónico
 * contra 16 del viejo**, y las 16 están todas en criterios que piden REDIRIGIRLO
 * —`R1.1-F2-CA-70` a `CA-75`, textual: *«Una dirección VIEJA con brazo lleva a la
 * nueva»*, `#/hibrido/participantes` queda en `#/brazos/hibrido/participantes`—.
 *
 * ⭐ La redirección **existe y está medida**: `canonicalizar()` en `rutas.ts`, con
 * `F2-070` a `F2-075` en verde. Una dirección vieja **no falla: se redirige**.
 *
 * ⚠️ **Por qué se enmienda y no se borra, y por qué vale la pena:** esta prosa no
 * rompía nada —el producto hace lo correcto— pero **mandaba a leer mal**.
 * `brazoDeLaDireccion` (`brazo-del-cable.ts:67`) exige el segmento `brazos`, y
 * quien leyera estas tres líneas concluiría que hay dos esquemas vivos y que esa
 * exigencia es un defecto. Pasó el 17/9/2026, y la hipótesis se cayó recién al
 * contar el PRD. ⛔ **`brazoDeLaDireccion` tiene razón y no se afloja.**
 *
 * ⚠️ **Lo que NO afirma esta enmienda:** nada sobre por qué cuatro pantallas montan
 * su cartel de error (`CP-R1-1-F1-…`, 15 casos bloqueados). Eso quedó **sin
 * determinar** y se mide contra el `admin-web` vivo, leyendo el `textoDelError`
 * real. Lo único que esta enmienda descarta es que la causa sea el esquema de
 * direcciones.
 *
 * ⚠️⛔ **Y ACÁ VA EL LÍMITE, MEDIDO, PORQUE ES FÁCIL LEER DE MÁS ESTE CAMBIO.**
 *
 * El brazo de la ruta es una declaración **de VISTA**, no de datos. Medido el
 * 22/8 sobre este árbol:
 *
 *   · `grep -rni "puro|hibrido" apps/management-ui/src` ⇒ **UN solo hit**, y es
 *     el TEXTO de la definición de una métrica. **El brazo no era estado de esta
 *     aplicación: no existía en ella en ninguna forma.**
 *   · Los seis bordes mandan el pedido **sin brazo, a propósito y escrito**:
 *     `borde-elegibles.ts` («no hay `?arm=` ni `?brazo=`. Lo resuelve `admin-web`
 *     de la sesión»), `borde-exportacion.ts` ②, `metricas/pantalla.tsx` guard ⑤
 *     («sin selector de brazo»). Es `ADR-0009` condición 5 + la RLS forzada.
 *
 * ⇒ **Poner el brazo en el cable NO cabe en este encargo**: cambiaría
 * `contracts/**` y `apps/admin-web/**`, que son territorio de otros agentes. Lo
 * que esta ruta hace es que la PANTALLA sepa qué brazo se está mirando; **quién
 * decide qué filas se leen sigue siendo el servidor, desde la sesión**. Si la URL
 * dice `hibrido` y la sesión es `puro`, lo que se ve son los datos del `puro` con
 * un rótulo que dice otra cosa — **no hay fuga entre brazos** (la RLS no depende
 * de esto), pero sí hay una inconsistencia de rótulo, y queda en el informe.
 *
 * ⚠️ **El brazo NO se valida contra una lista, y es deliberado.**
 * `contracts/platform/index.ts:138` declara `type Arm = string` y
 * `contracts/exports/index.ts:296-302` explica por qué: los brazos son `[CONFIG]`
 * por instancia (`annex-a-config.md` §Brazos e instancia), así que una constante
 * `"Híbrido"` o un `type Arm = "puro" | "hibrido"` «haría que la instancia que
 * declara otros nombres arranque con un contrato que no la describe». Esta app
 * no tiene forma de saber qué brazos declaró la instancia, así que trata el
 * segmento como el string opaco que el contrato dice que es.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ EL `dataProvider` Y LOS BORDES NO CAMBIARON
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los seis bordes se siguen construyendo acá y entran por contexto, con `useMemo`
 * por las razones que cada uno tiene anotadas abajo. **No se agregó ningún
 * `src/contexto-*.tsx` nuevo, y no es casualidad:** `gates/pantalla-envios.spec.ts`
 * DESCUBRE los contextos barriendo ese glob, así que un contexto nuevo cambia lo
 * que ese arnés monta. Lo que esta versión necesita pasarle a una pantalla va por
 * PROP, que es el precedente que `cargarElegibles` ya dejó en este mismo archivo.
 */

import { Fragment, useMemo } from "react"
import type { JSX, ReactNode } from "react"
import { Refine } from "@refinedev/core"
import routerProvider from "@refinedev/react-router"
import { HashRouter, Link, Navigate, NavLink, Outlet, Route, Routes, useLocation } from "react-router"
/*
 * ⭐ `R1.1-F2` · las dos piezas nuevas del modelo de navegación. `zonas.ts`
 * declara qué zonas existen —los dos brazos y las transversales— y `rutas.ts`
 * escribe la tabla de direcciones de `PRD_R1.1.md:302-325` como función pura.
 * Los dos están del lado de `src/` y no adentro de una pantalla porque son del
 * MARCO: doce directorios cuelgan de lo que digan.
 */
import {
  aLosBrazos,
  brazos as brazosDeclarados,
  derivacionDelBrazo,
  derivaAHumano,
  esBrazo,

  instancia,
  migaTransversal,
  nombreDelBrazo,
  selloDelBrazo,
  selloTransversal,
} from "./zonas.ts"
import type { Brazo } from "./zonas.ts"
import {
  INICIO,
  MENU_TRANSVERSAL,
  RUTA_DEL_BANCO,
  RUTA_DE_LA_CONFIGURACION_INICIAL,
  RUTA_ELEGIR_CONTRASENA,
  RUTA_LOGIN,
  SEGMENTOS,
  canonicalizar,
  destinoPorRol,
  esPantallaDelBrazo,
  esZonaDelRol,
  menuDelBrazo,
  necesitaElBrazoDelAcompanante,
  rutaDeLaPantalla,
  segmentosDe,
} from "./rutas.ts"
import type { PantallaDelMenu } from "./rutas.ts"
/** ⛔ `R2-UI6` · TIPO y nunca valor: ver `rutas.ts` y `ADR-0330`. */
import type { Role } from "@contracts/identity/index.ts"
/**
 * ⭐ `R2-UI6` · la sesión. Ver `sesion.tsx` para por qué es un contexto y por qué
 * el archivo **no** se llama `contexto-sesion.tsx`.
 */
import { BloqueDeSesion, ProveedorDeSesion, rolDeLaSesion, useSesion } from "./sesion.tsx"
/**
 * ⭐ `R2-UI19` · el brazo del Acompañante, leído de `GET /configuracion` de
 * `admin-web` (`ADR-0384` E). Ver `declaracion-publicada.ts`.
 */
import {
  crearLectorDelBrazoDelAcompanante,
  useBrazoDelAcompanante,
  type LectorDelBrazoDelAcompanante,
} from "./declaracion-publicada.ts"
/**
 * ⛔ `R2-UI6` · **`src/login/` se IMPORTA, no se edita**: es territorio de
 * `R2-UI5`. Lo único que esta etapa le pone son las props que `login/index.ts`
 * dejó pedidas por escrito —la raíz de `auth.<sitio>` y el destino por rol—,
 * porque *«nada de acá conoce el marco»*.
 */
import {
  PantallaElegirContrasena,
  PantallaLogin,
  crearBordeSesion,
  type BordeSesion,
} from "./login/index.ts"
import type { ConfiguracionInstancia } from "./configuracion.ts"
/*
 * ⭐ `R3-UI0` · `useConfiguracion` entra acá por `A15`: el TEXTO de la capacidad de
 * derivación sale de `derivacionPorBrazo`, que vive en la configuración de la
 * instancia (ver `zonas.ts` §`derivacionDelBrazo`). El contexto ya envolvía todo
 * (`<ProveedorConfiguracion>`, abajo, por FUERA de `<Routes>`), así que no hay
 * plomería nueva ni un contexto nuevo — y eso último importa: `gates/pantalla-envios.spec.ts`
 * descubre los contextos barriendo `src/contexto-*.tsx`, y un archivo más ahí
 * cambiaría lo que ese arnés monta (ver §`App` `bordeSesion`).
 */
import { ProveedorConfiguracion, useConfiguracion } from "./contexto-configuracion.tsx"
import { ProveedorBordeEscritura } from "./contexto-borde-escritura.tsx"
import { RECURSO_DOCUMENTOS, crearProveedorDatos } from "./proveedor-datos.ts"
import { crearBordeDeEscrituraHttp } from "./borde-escritura.ts"
import { crearBordeParticipantesHttp } from "./borde-participantes.ts"
import { ProveedorBordeParticipantes } from "./contexto-borde-participantes.tsx"
import { PantallaAlta } from "./alta.tsx"
import { TablaBancoDeContenido } from "./banco-de-contenido/tabla.tsx"
import { crearBordeConversacionesHttp } from "./conversaciones/borde-conversaciones.ts"
import { PantallaDeSituaciones, type BordeDeSituaciones } from "./situaciones-criticas/pantalla.tsx"
// ⟦R4-UIB1 · 5/10/2026⟧ El borde REAL de Situaciones críticas se define en este
// archivo (`crearBordeDeSituacionesHttp`, más abajo) porque el territorio de la
// etapa no incluye `src/situaciones-criticas/`. Necesita estos dos helpers y los
// tipos locales de la pantalla.
import { raizDelBrazo } from "./brazo-del-cable.ts"
import { mensajeDeError } from "./mensaje-de-error.ts"
import type { Atencion, Caso, Conversacion, MensajeDeConversacion } from "./situaciones-criticas/tipos.ts"
import {
  textoDeCategoria,
  textoDeEstadoDeMensaje,
  type CategoriaEnLaRed,
  type EstadoMensajeEnLaRed,
} from "./situaciones-criticas/cable.ts"
import { ProveedorBordeConversaciones } from "./contexto-borde-conversaciones.tsx"
import { TablaConversaciones } from "./conversaciones/tabla.tsx"
/*
 * ⭐⭐ `AW48` · la pantalla de configuración del chatbot OCUPA EL LUGAR DE LA
 * MAQUETA. `R1-F2 §1`: «Esta funcionalidad reemplaza el contenido de esa pantalla
 * y la renombra a "Configuración del chatbot", porque pasa a contener personalidad
 * y reglas de contención, que no son mensajes.»
 *
 * ⛔⛔ **RASTRO · 23/9/2026 · ESTE RENGLÓN YA NO ES CIERTO.** Decía
 * ~~«La maqueta NO se borra del árbol: `src/respuestas-automaticas/pantalla.tsx`
 * queda intacta y sigue siendo el sujeto de su propia etapa»~~. **`R2-UI8`
 * (`ADR-0348`) la BORRÓ**, con `pruebas/aw8-respuestas-automaticas.test.tsx`,
 * por `PRD_R2.md:2494` («El archivo se borra») y `B24`: su funcionalidad vive
 * ahora en la pestaña ② de Plantillas (`src/plantillas/botones/**`), porque
 * `R2-F3-CA-04` prohíbe el ítem de menú del que ese directorio tomaba el nombre.
 * `AW8` queda como etapa **cumplida-y-retirada**. Lo de abajo —qué se monta y
 * cómo se llama el renglón del menú— **sigue valiendo entero**.
 * Lo que cambia acá es qué se monta y cómo se llama el renglón del menú — que es
 * exactamente lo que `R1-F2-21` pide y ni un milímetro más.
 *
 * ⚠️ Y el veredicto de las dos etapas sobre esta superficie es OPUESTO —una pide
 * una maqueta inerte, la otra una pantalla que escribe—, así que el examen de
 * aquélla se pone rojo con este cambio. Está previsto y medido del otro lado; no
 * se apaga desde acá.
 */
import { PantallaConfiguracionDelChatbot } from "./configuracion-del-chatbot/pantalla.tsx"
import { crearBordeConfiguracionHttp } from "./configuracion-del-chatbot/borde-configuracion.ts"
import { ProveedorBordeConfiguracion } from "./contexto-borde-configuracion.tsx"
import { crearBordeEnviosHttp } from "./envios/borde-envios.ts"
import { ProveedorBordeEnvios } from "./contexto-borde-envios.tsx"
import { PantallaEnvios } from "./envios/pantalla.tsx"
import type { CargarListaParaEnvios } from "./envios/formulario.tsx"
import { crearCargarElegiblesHttp } from "./envios/borde-elegibles.ts"
import { crearBordeExportacionHttp } from "./exportacion/borde-exportacion.ts"
import { ProveedorBordeExportacion } from "./contexto-borde-exportacion.tsx"
import { PantallaExportacion } from "./exportacion/pantalla.tsx"
import { SEGMENTO_BRAZO, useBrazo } from "./brazo.ts"
import { crearBordePlantillasHttp, SIN_CATALOGO_DE_PLANTILLAS } from "./plantillas/borde-plantillas.ts"
import type { BordePlantillas, CargarPlantillas } from "./plantillas/borde-plantillas.ts"
import { PantallaPlantillas } from "./plantillas/pantalla.tsx"
import {
  IconoAlta,
  IconoCargaEnLote,
  IconoConversaciones,
  IconoBancoDeContenido,
  IconoEnvios,
  IconoExportacion,
  IconoMetricas,
  IconoParticipantes,
  IconoPlantillas,
  IconoCambiarDeBrazo,
  IconoConfiguracionDelChatbot,
  IconoSimulador,
} from "./iconos.tsx"
import type { IconoDeArea } from "./iconos.tsx"
import { crearBordePadronHttp } from "./padron/borde-padron.ts"
import { TablaPadron } from "./padron/tabla.tsx"
import { crearBordeMetricasHttp } from "./metricas/borde-metricas.ts"
import { ProveedorBordeMetricas } from "./contexto-borde-metricas.tsx"
import { PantallaMetricas } from "./metricas/pantalla.tsx"
import { crearBordeCargaHttp } from "./carga-en-lote/borde-carga.ts"
import { PantallaCargaEnLote } from "./carga-en-lote/pantalla-carga.tsx"
import { bordeSimuladorDeLaInstancia } from "./simulador/borde-simulador.ts"
import type { BordeSimulador } from "./simulador/borde-simulador.ts"
import { PantallaSimulador } from "./simulador/chat.tsx"

/**
 * El segmento de ruta de cada pantalla, en UN solo lugar.
 *
 * Están acá y no repartidos por el archivo porque son el único acuerdo nuevo que
 * este cambio introduce, y porque la barra de navegación y la tabla de rutas
 * tienen que decir lo mismo: si divergen, el enlace lleva a una ruta que cae en
 * el comodín y la pantalla desaparece sin ningún error a la vista.
 */
/*
 * ⛔⛔ `R1.1-F2` · RASTRO `[17/9/2026]` — **LA TABLA DE SEGMENTOS SE MUDÓ A
 * `./rutas.ts`, Y CAMBIÓ DE FORMA.**
 *
 * Acá vivían los once segmentos de `ADR-0136`: una pantalla por segmento suelto
 * (`documentos`, `metricas`, `cargas`…) y el brazo como UN prefijo de un solo
 * segmento (`#/hibrido/metricas`). ~~Cada pantalla tiene una ruta que la monta, y
 * sólo ella, bajo `/` y bajo `/:brazo`.~~
 *
 * ⭐ **Lo deroga `docs/prd/PRD_R1.1.md:302-325`**, que es una tabla de veinte
 * renglones con un modelo distinto: `#/brazos/<brazo>/<pantalla>` para lo del
 * brazo, `#/instancia/<pantalla>` para lo transversal, `#/` para el Inicio, y
 * **redirección** de todas las direcciones viejas. El cambio no es de nombres:
 * las transversales dejan de estar adentro del brazo (`PRD_R1.1.md:358`,
 * `R1.1-F2-CA-20`), así que la lista vieja ya no describe un menú que exista.
 *
 * ⚠️ **Nada de lo viejo se borra: se mueve a donde tiene sentido.** Los once
 * segmentos de `ADR-0136` siguen escritos, en `rutas.ts` §`RUTAS_VIEJAS_CON_BRAZO`
 * y §`RUTAS_VIEJAS_SIN_BRAZO`, que es su lugar nuevo: **el origen de una
 * redirección**. `#/hibrido/metricas` sigue llevando a Métricas del híbrido; lo
 * que cambia es que ahora la barra queda diciendo `#/brazos/hibrido/metricas`.
 */

/*
 * ⭐ `AW15b` · `PARAMETRO_BRAZO`, `SEGMENTO_BRAZO` y `useBrazo()` SE MUDARON a
 * `./brazo.ts`, enteros y sin copia.
 *
 * Vivían acá desde `ADR-0136` porque este archivo era el único que los usaba.
 * `AW15b` necesita el brazo en `padron/tabla.tsx` —el cuerpo de
 * `PATCH participantes/{id}/flag` lo exige y la fila del padrón no lo trae ni
 * puede traerlo (`ADR-0191`, ocho campos sin `arm`)— y la alternativa a mudarlo
 * era un SEGUNDO lector del mismo parámetro de ruta, que es la forma exacta de
 * que los dos divergan sin que nada se ponga rojo. El riesgo de exportarlo está
 * medido y escrito en el encabezado de `./brazo.ts`.
 */

/**
 * ⭐ `R1.1-F2` · **EL ÍCONO DE CADA RENGLÓN DEL MENÚ, POR SU SEGMENTO.**
 *
 * ── LO QUE ACÁ HABÍA, TEXTUAL (no se borra) ────────────────────────────────
 *
 *   ~~`type Renglon = readonly [segmento, etiqueta, Icono]`~~ · *«⛔⭐ `AW34` ·
 *   EL ÍCONO ES EL TERCER ELEMENTO DE LA MISMA TERNA, Y NO UNA TABLA APARTE. Un
 *   `Record<segmento, Icono>` al lado de esta lista serían dos listas que tienen
 *   que coincidir escritas en dos lugares […] Acá no se puede agregar un área sin
 *   decidir su ícono: `tsc` lo exige.»*
 *
 *   ~~`const NAVEGACION: readonly Renglon[]`~~ — las DIEZ áreas, con «Banco de
 *   Contenido» y «Configuración del chatbot» **adentro del menú del brazo**.
 *
 *   ~~`const RENGLON_SIMULADOR`~~ — la undécima, condicionada a `botWebUrl`.
 *
 * ⛔ **Lo deroga `docs/prd/PRD_R1.1.md:358` (`R1.1-F2-CA-19`) y `:359`
 * (`CA-20`):** el menú del brazo lista, en orden, *«Participantes (Alta manual,
 * Carga en lote), Conversaciones, Plantillas, Envíos (Nuevo envío), Exportación,
 * Métricas y Simulador»*, y *«el menú de KAI · Brazo Híbrido no incluye Banco de
 * contenido ni Configuración del chatbot»*. Las transversales se mudan a su
 * propio marco (`PRD_R1.1.md:468`). ⇒ la lista deja de ser «las áreas de la app»
 * y pasa a ser **el menú de una zona**, así que vive con la tabla de direcciones,
 * en `rutas.ts` §`MENU_DEL_BRAZO` y §`MENU_TRANSVERSAL`.
 *
 * ⭐⭐ **Y LA PROPIEDAD QUE EL PÁRRAFO VIEJO PROTEGÍA SE CONSERVA, POR OTRO
 * MECANISMO.** Un `Record<string, IconoDeArea>` NO la daría —una clave de menos
 * devuelve `undefined` y el renglón sale sin ícono, que es el defecto exacto que
 * `AW34` describía—. Por eso la clave es `PantallaDelMenu`, el union de los
 * segmentos que el menú nombra: **un renglón nuevo sin ícono no compila.** La
 * exigencia de `tsc` no se aflojó, se mudó de forma.
 *
 * ⚠️ El simulador tiene su ícono acá aunque su renglón sea condicional: la
 * condición es de la RUTA y del MENÚ (`rutas.ts` §`RENGLON_DEL_SIMULADOR`), no de
 * si el dibujo existe.
 */
const ICONO_DE_LA_PANTALLA: Record<PantallaDelMenu, IconoDeArea> = {
  [SEGMENTOS.participantes]: IconoParticipantes,
  [SEGMENTOS.alta]: IconoAlta,
  [SEGMENTOS.cargaEnLote]: IconoCargaEnLote,
  [SEGMENTOS.conversaciones]: IconoConversaciones,
  [SEGMENTOS.plantillas]: IconoPlantillas,
  [SEGMENTOS.envios]: IconoEnvios,
  [SEGMENTOS.nuevoEnvio]: IconoEnvios,
  [SEGMENTOS.exportacion]: IconoExportacion,
  [SEGMENTOS.metricas]: IconoMetricas,
  [SEGMENTOS.simulador]: IconoSimulador,
  /*
   * ⭐ `R4-UIB1` · ⟦decisión 11⟧ el renglón «Situaciones críticas» REUSA un ícono
   * EXISTENTE de `iconos.tsx`; `guard-icono-existente` prohíbe declarar uno propio
   * acá. La clave va como literal —y no `[SEGMENTOS.…]`— porque el gate la lee como
   * `clave: Icono…` y necesita reconocerla como la de la pantalla nueva.
   */
  "situaciones-criticas": IconoConversaciones,
}

/** El ícono de cada renglón del marco transversal. `R1.1-F3-CA-06`. */
const ICONO_TRANSVERSAL: Record<string, IconoDeArea> = {
  [RUTA_DEL_BANCO]: IconoBancoDeContenido,
  [RUTA_DE_LA_CONFIGURACION_INICIAL]: IconoConfiguracionDelChatbot,
}

/**
 * ⭐⭐ `R1.1-F2` · **EL MARCO DE UNA ZONA** — el rail, el sello y el hueco donde
 * se dibuja la pantalla.
 *
 * ── LO QUE `Marco` DECÍA, TEXTUAL (no se borra) ────────────────────────────
 *
 *   *«El marco: la barra de navegación y el hueco donde se dibuja la pantalla.
 *   ⭐ Los enlaces son RELATIVOS y eso es lo que los hace baratos. Un `to`
 *   relativo se resuelve contra la ruta del marco, así que el mismo marco sirve
 *   montado en `/` y montado bajo un prefijo, sin que ningún enlace tenga que
 *   acordarse del prefijo.»*
 *
 * ⭐ **Eso sigue siendo cierto y sigue siendo el mecanismo**: los renglones del
 * menú del brazo se siguen resolviendo contra `/brazos/<brazo>`, así que ninguno
 * escribe el brazo. Lo que cambió es que **hay DOS marcos y no uno**, porque el
 * PRD pide dos zonas con menús distintos: el del brazo (`PRD_R1.1.md:300`,
 * `R1.1-F2-CA-19`) y el neutro de las transversales (`PRD_R1.1.md:468`,
 * `R1.1-F3-CA-06`). Lo común —el rail, el interruptor de ancho, el salto al
 * contenido, el `<main>`— vive en `RailYContenido` y no se escribe dos veces.
 *
 * ⛔ Ninguno se exporta: `configuracion.ts` deja anotado que el gate de `AW10`
 * descubre componentes **montando cada export de `src/**`**, y un marco fuera de
 * un enrutador no tiene sentido.
 */
function RailYContenido({
  sello,
  renglones,
  miga,
}: {
  readonly sello: JSX.Element
  readonly renglones: readonly JSX.Element[]
  readonly miga: string | null
}): JSX.Element {
  /**
   * ⛔⭐⭐ `AW34` / `ADR-0234` · **EL RAIL YA NO COLAPSA — decisión del dueño del
   * 21/9/2026, tomada con `ADR-0234` a la vista.**
   *
   * Acá vivía `const [railAncho, setRailAncho] = useState(true)` y el botón
   * «Angostar el menú» / «Ensanchar el menú». El motivo del borrado: **ningún
   * criterio del PRD nombra el control y ningún boceto de R1.1 lo dibuja**, así
   * que por `ADR-0319 ②` manda el boceto. `grep "Angostar el menú"` sobre
   * `docs/prd/PRD_R1.1.md` y sobre los 31 bocetos daba **cero**.
   *
   * ⚠️ **Lo que esto revierte, dicho entero:** `ADR-0234` nació de una corrección
   * del dueño mirando la pantalla construida —*«el ocultar no es vertical, sino
   * hacia un lado, quiero el comportamiento típico de los sidebars donde queda un
   * sidebar solo de íconos o un sidebar completo»*— y fue lo que obligó a crear
   * la primera iconografía del producto. Los diez íconos **se quedan**: siguen
   * siendo el ícono de cada área. Lo que se fue es el segundo estado.
   *
   * ⛔ **`data-rail="ancho"` SE QUEDA, fijo, y no es un resto.** La hoja lo usa en
   * dos familias distintas: `[data-rail="ancho"]` da los 17rem del rail, y
   * **`[data-rail]` A SECAS sostiene el layout de teléfono** (`estilos.css:2323`:
   * *«Ahora cuelga de `[data-rail]` a secas: la fila está siempre»*). Sacar el
   * atributo no borraba un estado: rompía el responsive.
   */
  return (
    <>
      {/*
        El enlace de salto: primero en el orden de tabulación, invisible hasta que
        recibe el foco. `AW34` · Queda FUERA de `.marco` a propósito.
      */}
      <a className="salto" href="#contenido">
        Saltar al contenido
      </a>
      <div className="marco" data-rail="ancho">
        <nav className="navegacion" aria-label="Áreas del Management Tool">
          {sello}
          <ul id="navegacion-areas">
            {renglones.map((renglon) => (
              <li key={renglon.key}>
                {/*
                  ⛔ `AW34` · El ícono va `aria-hidden` (lo pone `iconos.tsx`) y el
                  nombre accesible del enlace es SIEMPRE el rótulo de texto. El
                  ícono no es un segundo anuncio.

                  ⚠️ Acá había un `cloneElement(renglon, { claseDeRotulo })`: el
                  marco le pasaba a cada renglón si su rótulo iba visible u oculto,
                  «porque quién es visible depende del ancho y el ancho vive acá».
                  Con el rail sin segundo estado **el rótulo está siempre visible**,
                  así que el renglón se monta tal cual y `RenglonDelMenu` usa su
                  clase fija.
                */}
                {renglon}
              </li>
            ))}
          </ul>
          {/*
            ⭐⭐ `R2-UI6` · **EL BLOQUE DE SESIÓN, AL PIE DEL RAIL.** Regla 23
            (PRD:2321), PRD:2179-2184, `CA-F6-48`, `CA-F6-49`, `CA-F6-50`:
            *«debajo del último renglón del menú»*. `// ADR-0353`

            ⛔ **Es una INSERCIÓN, no un reemplazo**: acá no había nada — entre el
            `</ul>` y el `</nav>` el rail estaba vacío. Medido el 22/9/2026.

            ⭐ **Un solo cambio sirve a los dos marcos.** `MarcoDelBrazo` y
            `MarcoTransversal` llaman los dos a este componente, así que el pie
            aparece en el brazo (`CA-F6-48`) y en las transversales (`CA-F6-49`)
            sin tocar ninguno de los dos — y no pueden divergir.

            ⛔ **Y va adentro del `<nav>`, después del `<ul>`.** Afuera sería «al
            lado del menú» y no «debajo del último renglón»; adentro del `<ul>`
            sería un ítem más del menú, que es lo que `CA-F6-29`/`CA-F6-30` cuentan
            (el Acompañante no tiene ningún renglón, pero **sí** tiene su bloque de
            sesión: `CA-F6-50`).

            ⚠️ **No recibe prop**: lee el contexto. Si recibiera el actor por prop
            habría que pasarlo desde `Rutas` a través de dos `element=` de
            `<Route>`, que es justamente lo que no se puede hacer. Ver `sesion.tsx`.
          */}
          <BloqueDeSesion />
        </nav>
        {/*
          ⭐ EL LANDMARK PRINCIPAL, y arriba de él **la MIGA**.
          `R1.1-F2-CA-42`…`CA-51`: toda pantalla del brazo repite la zona en un
          texto chico arriba del título. Va en el marco y no en cada pantalla
          porque es del marco: once pantallas que la escribieran cada una son once
          lugares donde puede decir otra cosa que el sello.
        */}
        <main id="contenido">
          {miga !== null && <p className="miga">{miga}</p>}
          <Outlet />
        </main>
      </div>
    </>
  )
}

/**
 * Un renglón del menú. El rótulo se vuelve invisible —no ausente— en el rail
 * angosto: `sim__oculto` lo saca de la pantalla y lo deja en el árbol de
 * accesibilidad. ⛔ No es `display:none` ni `visibility:hidden`, que SACAN del
 * árbol, y un rail de sólo íconos sin nombre accesible es un menú inutilizable
 * para quien no ve (`ADR-0234` §«Lo que NO cambia»).
 */
function RenglonDelMenu({
  a,
  etiqueta,
  Icono,
  sangria = false,
  relativo = false,
}: {
  readonly a: string
  readonly etiqueta: string
  readonly Icono: IconoDeArea
  readonly sangria?: boolean
  readonly relativo?: boolean
}): JSX.Element {
  return (
    /*
     * ⚠️ `className` y `relative` se arman con el `{...(… ? {x} : {})}` que este
     * archivo ya usa para `brazos`: con `exactOptionalPropertyTypes` prendido,
     * `className={undefined}` y la prop AUSENTE **no son lo mismo**, y `NavLink`
     * no acepta el primero.
     */
    <NavLink
      to={a}
      end
      {...(sangria ? { className: "navegacion__sub" } : {})}
      {...(relativo ? { relative: "route" as const } : {})}
    >
      <Icono />
      <span className="navegacion__rotulo">{etiqueta}</span>
    </NavLink>
  )
}

/**
 * ⭐⭐ **EL MARCO DEL BRAZO.** `PRD_R1.1.md:300`.
 *
 * Sello «KAI · Brazo X» con su descripción, «Cambiar de brazo», el menú de las
 * pantallas del brazo, y la miga.
 *
 * ⚠️ **El sello ya no dice «Brazo: hibrido».** Decía eso —el nombre crudo del
 * segmento— y lo deroga `R1.1-F2-CA-32`: *«en `#/brazos/puro/participantes` el
 * sello dice “KAI · Brazo Puro”»*. El nombre crudo sobrevive en `data-brazo`,
 * que es lo que `estilos.css` §① lee para teñir `--senal`. ⟦rastro · 3/11/2026⟧
 * ~~y en la abreviatura del rail angosto~~.
 */
function MarcoDelBrazo({
  brazo,
  conSimulador,
  rol,
}: {
  readonly brazo: Brazo
  readonly conSimulador: boolean
  readonly rol: Role
}): JSX.Element {
  /*
   * ⭐⭐ `R2-UI6` · **EL MENÚ POR ROL, DECIDIDO EN `rutas.ts` Y NO ACÁ.**
   * `// ADR-0353` · `CA-F6-24`, `CA-F6-29`, `CA-F6-30`; `R1.1-F2-CA-19`/`CA-21`
   * reescritos con rol (A11).
   *
   * ⛔ **Acá no se pregunta por el nombre del rol.** `menuDelBrazo` le pregunta a
   * `destinoPorRol` —la única función que sabe dónde cae cada rol—, así que un
   * `if (rol === "acompanante")` no existe en este archivo. Ver el guard de
   * `r2-ui6.md:64`: el menú se DERIVA, no se filtra.
   *
   * ⚠️ Para el Administrador esto devuelve **exactamente** `MENU_DEL_BRAZO` (más
   * el Simulador si la instancia lo declaró) y `conCambiarDeBrazo: true`: el
   * comportamiento de R1.1, entero y sin `filter` de por medio.
   */
  const { conCambiarDeBrazo, renglones: renglonesDelMenu } = menuDelBrazo(rol, conSimulador)

  /*
   * ⭐ `A15`/`R3-E10` · la configuración de la instancia, para el TEXTO de la
   * capacidad de derivación del sello. `null` («no declarada») ⇒ negada; la lee
   * `zonas.ts` §`derivacionDelBrazo` con la misma función que el panel de botones.
   */
  const configuracion = useConfiguracion()

  /*
   * ⭐ El sello lleva TRES portadores del brazo y dos no son cromáticos
   * (`ADR-0234` §②): ① el color, que `body:has([data-brazo="…"])` tiñe; ② el
   * patrón del filete, que sobrevive al alto contraste forzado y a la ceguera al
   * color; ③ **texto que se LEE** — el sello entero, visible y en el nombre
   * accesible. ⟦rastro · 3/11/2026⟧ ~~la abreviatura de tres letras en angosto~~.
   */
  const sello = (
    <p className="navegacion__brazo" data-brazo={brazo}>
      <span className="sim__oculto">{selloDelBrazo(brazo)}</span>
      {/* ⟦rastro · 3/11/2026⟧ ~~`abreviarBrazo(brazo)` visible (`HIB`/`PUR`)~~: era la abreviatura del rail angosto, que no existe desde `AW34`; el boceto no la dibuja. */}
      <span className="navegacion__brazo-nombre" aria-hidden="true">
        {selloDelBrazo(brazo)}
        <span className="navegacion__brazo-derivacion">
          {derivacionDelBrazo(configuracion, brazo)}
        </span>
      </span>
    </p>
  )

  const renglones = [
    /*
     * ⭐ «Cambiar de brazo» lleva a Inicio, `R1.1-F2-CA-21`. Es la ÚNICA salida
     * del brazo que el menú ofrece —las transversales no están (`CA-20`)—, así
     * que va primero: sin ella, salir del brazo exigiría editar la barra.
     *
     * ⛔ `R2-UI6` · **`CA-F6-29`: el Acompañante NO lo tiene**, y no por un
     * `filter`: el renglón lleva a Inicio, y quien no puede estar en Inicio no
     * puede tener un renglón que lleve ahí. La condición la contesta
     * `menuDelBrazo` preguntándole a `destinoPorRol`.
     */
    ...(conCambiarDeBrazo
      ? [
          <RenglonDelMenu
            key="cambiar"
            a={INICIO}
            etiqueta="Cambiar de brazo"
            Icono={IconoCambiarDeBrazo}
          />,
        ]
      : []),
    ...renglonesDelMenu.map(([segmento, etiqueta, sangria]) => (
      /*
       * ⭐ `to` RELATIVO: se resuelve contra `/brazos/<brazo>`, así que ningún
       * renglón escribe el brazo y no hay diez lugares donde olvidarlo. Es el
       * mecanismo que el marco viejo ya usaba y la razón por la que el brazo va
       * de SEGMENTO y no de `?brazo=`.
       */
      <RenglonDelMenu
        key={segmento}
        a={segmento}
        etiqueta={etiqueta}
        Icono={ICONO_DE_LA_PANTALLA[segmento]}
        sangria={sangria}
        relativo
      />
    )),
  ]

  return <RailYContenido sello={sello} renglones={renglones} miga={selloDelBrazo(brazo)} />
}

/**
 * ⭐⭐ **EL MARCO NEUTRO DE LAS TRANSVERSALES.** `PRD_R1.1.md:468`,
 * `R1.1-F3-CA-01`, `CA-02`, `CA-06`…`CA-09`.
 *
 * ⛔ **Sin `data-brazo`, y eso es la mitad del punto.** `estilos.css` §① tiñe
 * `--senal` con `body:has([data-brazo="…"])`; sin el atributo, la señal vuelve a
 * `--tinta` y la zona queda GRIS, que es exactamente lo que el PRD pide («sello
 * gris "KAI · Transversales"»). El color no se apaga con una regla nueva: se
 * apaga por no declarar el brazo, porque acá **no hay** brazo.
 */
function MarcoTransversal(): JSX.Element {
  const sello = (
    <p className="navegacion__brazo navegacion__brazo--transversal">
      <span className="sim__oculto">{selloTransversal()}</span>
      {/* ⟦rastro · 3/11/2026⟧ ~~«TRA» visible~~: abreviatura del rail angosto (no existe desde `AW34`); `BancoDeContenido.dc.html` no la dibuja. */}
      <span className="navegacion__brazo-nombre" aria-hidden="true">
        {selloTransversal()}
        {/* ⟦rastro · 30/9/2026⟧ ~~«Aplica a los dos brazos»~~ fijo, que `OLI` (un brazo) mostraba igual. */}
        <span className="navegacion__brazo-derivacion">Aplica {aLosBrazos()}</span>
      </span>
    </p>
  )

  const renglones = MENU_TRANSVERSAL.map(([a, etiqueta]) => (
    <RenglonDelMenu
      key={a}
      a={a}
      etiqueta={etiqueta}
      /*
       * ⚠️ «Volver al inicio» no está en `ICONO_TRANSVERSAL` y cae en
       * `IconoCambiarDeBrazo`: es la misma acción —salir de la zona— que el
       * renglón homónimo del brazo, así que lleva el mismo dibujo a propósito.
       */
      Icono={ICONO_TRANSVERSAL[a] ?? IconoCambiarDeBrazo}
    />
  ))

  /*
   * ⛔ La miga NO es el sello, y hasta hoy acá iba `SELLO_TRANSVERSAL`. El boceto
   * pide «KAI · Funcionalidades transversales» en la miga y el PRD pide
   * «KAI · Transversales» en el sello: ocho criterios (`F5-CA-07`…`CA-28`) contra
   * dos (`F3-CA-01`/`CA-02`). Ver `zonas.ts` §`MIGA_TRANSVERSAL`.
   */
  return <RailYContenido sello={sello} renglones={renglones} miga={migaTransversal()} />
}

/**
 * ⭐ **INICIO (`#/`).** `PRD_R1.1.md:296-298` y `R1.1-F2-CA-01`…`CA-18`.
 *
 * ⛔ **No tiene marco y no lee datos de brazo** (`CA-15`): es la única pantalla
 * de la consola que no está parada en ninguna zona, así que no monta el rail —no
 * habría menú que mostrar— y no dispara ningún pedido.
 */
function Inicio(): JSX.Element {
  /*
   * ⭐ `A15`/`R3-E10` · la misma lectura que el sello del menú. Ver
   * `zonas.ts` §`derivacionDelBrazo`: `null` ⇒ «Sin derivación a tutores».
   *
   * ⚠️ El `sort` de abajo sigue usando `derivaAHumano`, y **no es un descuido**:
   * lo que ordena las cajas es `R1.1-F2-CA-03` («el brazo que deriva a la
   * izquierda»), no el texto de la capacidad, y `derivaAHumano` es la función que
   * ese criterio ya tenía verde. Las dos lecturas son distintas a propósito.
   */
  const configuracion = useConfiguracion()

  /*
   * ⛔⛔⛔ `[22/9/2026]` · **EL ORDEN LO DA LA CAPACIDAD, Y ESTO NO ES UN ADORNO:
   * SIN ESTAS TRES LÍNEAS, `R1.1-F2-CA-03` SE PONE ROJO EN EL AMBIENTE
   * DESPLEGADO.**
   *
   * `CA-03` (`PRD_R1.1.md:521`): *«Los brazos tienen orden fijo: en Inicio la caja
   * “KAI · Brazo Híbrido” está **a la izquierda** de “KAI · Brazo Puro”»*.
   *
   * ⚠️ **Medido, corriendo el entrypoint contra la declaración real** (22/9/2026):
   * `configuracion.json` trae `"brazos": ["puro","hibrido"]` — **el Puro
   * PRIMERO**. Sale de `STUDY_ARMS=puro,hibrido` (`qa/entorno/env/comun.env:14`),
   * que es de donde lo derivan tanto el entrypoint de Docker como
   * `qa/entorno/levantar.sh`, y `UI_BRAZOS` **no está declarada en ningún lado**
   * del árbol. ⇒ iterar la declaración **en su orden crudo** dibujaría el Puro a
   * la izquierda en toda instancia desplegada.
   *
   * ⛔ Hasta hoy esto no pasaba porque el orden lo fijaba la constante
   * `BRAZOS = ["hibrido", "puro"]`, con el híbrido escrito primero. Leer los
   * brazos de la declaración **sin ordenarlos** cambia un verde por un rojo, y es
   * exactamente el tipo de regresión silenciosa que el cambio venía a eliminar.
   *
   * ⭐ **La traducción correcta del criterio NO es «`hibrido` primero» sino «el
   * brazo que DERIVA primero»** (`PRD_R1.md:194`: *«el comportamiento no se ata al
   * nombre del brazo, se ata a una capacidad declarada»*). Sobre `KAI` las dos
   * frases dan el mismo resultado —que es lo que mantiene `CA-03` verde—; sobre
   * una instancia con otros nombres sólo la segunda significa algo.
   *
   * ⚠️ **`sort` es ESTABLE** (ES2019 en adelante), así que los brazos con la misma
   * capacidad conservan el orden en que la instancia los declaró. Con
   * `brazoHibrido` ausente `derivaAHumano` es `true` para todos ⇒ ninguno se mueve
   * y el orden es, exacto, el de la declaración.
   *
   * ⛔ **DUEÑO: TL.** Esto ORDENA lo que la instancia declaró, y eso es una
   * decisión de producto que este archivo está tomando para no introducir una
   * regresión. La alternativa —dejar el orden crudo y corregir `STUDY_ARMS` a
   * `hibrido,puro`— vive en `qa/` e `infra/`, fuera de este territorio, y cambiaría
   * el orden para los TRES procesos que leen esa variable. Queda dicho acá para
   * que la elección se pueda revisar en vez de descubrirse.
   */
  const brazosEnOrden = [...brazosDeclarados()].sort(
    (a, b) => Number(derivaAHumano(b)) - Number(derivaAHumano(a)),
  )
  return (
    <>
      <a className="salto" href="#contenido">
        Saltar al contenido
      </a>
      <div className="inicio">
        <header className="inicio__barra">
          <p className="inicio__producto">Consola de gestión del estudio</p>
          {/*
            ⭐⭐ `R2-UI6` · **ACÁ DECÍA `<p className="inicio__sesion">Sesión: —</p>`,
            Y ESO ERA UNA MAQUETA DECLARADA.** `// ADR-0353`

            ⚠️ **Se REDACTA y no se borra** (`ADR-0082`): `PRD_R1.1.md` §1.3
            listaba «Indicador de sesión “Sesión: —”» entre lo que se ve con el
            diseño nuevo **y no hace nada**, y `R1.1-F2-CA-16` pedía ese texto
            exacto, sin nombre de usuario. Era cierto mientras la consola no tenía
            puerta.

            ⭐ Lo deroga `CA-F6-46` (PRD:2400), textual: *«con la sesión de Admin
            Uno, en `#/` se lee “Sesión: Admin Uno” **donde antes decía “Sesión:
            —”**»* — el criterio nombra el texto viejo, así que la derogación es
            explícita y no una interpretación. `CA-F6-47` agrega el «Cerrar
            sesión» al lado.

            ⛔ **Y es el MISMO componente que el pie del rail** (Regla 23): dos
            `<p>` escritos por separado divergen en cuanto uno agrega el rol.
          */}
          <BloqueDeSesion />
        </header>
        <main id="contenido" className="inicio__contenido">
          {/*
            ⭐ El rótulo «Instancia» sobre el título, tal como el boceto
            `docs/prd/R1.1_bocetos/pantallas/Main.dc.html` lo dibuja
            (`R1.1-F5-CA-02`, el orden de bloques). Es `aria-hidden` porque no
            aporta nada al árbol de accesibilidad —el `<h1>` ya dice «KAI» y el
            rótulo sólo lo clasifica visualmente— y anunciarlo antes del título
            haría que un lector de pantalla lea «Instancia KAI» como si fuera el
            nombre.
          */}
          {/*
            ⚠️ `[22/9/2026]` · El rótulo se dibuja **sólo si hay nombre de
            instancia que rotular**. Sin declaración, un rótulo «Instancia» sobre
            un título que no la nombra es peor que no estar: afirma que lo de
            abajo es el nombre de la instancia cuando no lo es.
          */}
          {instancia() !== null && (
            <p className="inicio__rotulo" aria-hidden="true">
              Instancia
            </p>
          )}
          {/*
            ⛔ El `<h1>` **no puede quedar vacío** —es el encabezado de la
            pantalla y el ancla de «Saltar al contenido»—, así que sin nombre
            declarado cae al nombre del PRODUCTO, que no es un nombre de
            instancia inventado sino lo que esta pantalla es. Ver `zonas.ts`
            ⟦la-declaracion-se-instala-antes-de-montar⟧.
          */}
          <h1>{instancia() ?? "Consola de gestión del estudio"}</h1>
          <p className="inicio__eleccion">
            Elige el brazo en el que vas a trabajar. Todo lo que veas, cargues, edites, elimines o
            exportes adentro queda en ese brazo.
          </p>

          {/*
            ⛔⛔ `[22/9/2026]` · **UNA CAJA POR BRAZO DECLARADO, Y LA SECCIÓN
            ENTERA DESAPARECE SI NO HAY NINGUNO.**

            ~~Antes esto era `BRAZOS.map(…)` sobre la constante de `zonas.ts`, así
            que dibujaba DOS cajas siempre.~~ Medido sobre `OLI`, que declara
            `"brazos": ["hibrido"]`: la consola dibujaba «Brazo Híbrido» **y
            «Brazo Puro»**, y el botón «Entrar» del segundo llevaba a una zona que
            `admin-web` rechaza («el primer segmento del camino tampoco es un
            brazo que esta instancia declare»). La consola era el único lugar que
            no honraba la declaración.

            ⚠️ **`R1.1-F1-CA-01` sigue verde y no es casualidad**: pide que en
            `KAI` se vean «KAI · Brazo Híbrido» y «KAI · Brazo Puro» **y ninguna
            otra**. `KAI` declara esos dos, en ese orden, así que se ven esos dos,
            en ese orden. El criterio se cumple **por la declaración** en vez de
            por una constante — que es lo que `R1-F1 §6` pide.

            ⛔ Sin brazos declarados la sección no se dibuja: un `<h2>Brazos del
            estudio</h2>` sobre un hueco afirma que los hay.
          */}
          {brazosEnOrden.length > 0 && (
          <section className="inicio__zona" aria-labelledby="inicio-brazos">
            <h2 id="inicio-brazos">Brazos del estudio</h2>
            {/*
              ⭐ **El orden lo fija la DECLARACIÓN** —`configuracion.json` los trae
              en su orden y `brazos()` lo preserva—, que en `KAI` es el híbrido
              primero (`CA-03`). Todas las cajas se dibujan con el MISMO bloque:
              `CA-04` y `CA-05` piden que midan igual, y dos JSX distintos serían
              dos lugares donde una puede crecer sin que nada avise.
            */}
            <div className="inicio__cajas">
              {brazosEnOrden.map((brazo) => (
                /*
                  ⛔⛔ `data-caja-brazo` y **NO `data-brazo`**, y es un defecto que
                  costó medir: `estilos.css` §① tiñe `--senal` con
                  `body:has([data-brazo="…"])`, y en Inicio hay **DOS** cajas. Con
                  `data-brazo` las dos reglas matchean, gana la última del archivo
                  y **la página entera queda del color de un brazo** — incluido el
                  botón «Entrar» del otro. Es exactamente lo que `PRD_R1.1.md:786`
                  (regla 7 de F5) prohíbe: *«El color de un brazo no se usa fuera
                  de su sello, miga, botón principal, pestaña activa, selección y
                  foco»*, y lo que `PRODUCT.md` §Positioning llama contaminar el
                  ensayo.

                  ⇒ el atributo es otro y el color se declara **en la caja**, así
                  que `--senal` queda acotado a su subárbol: cada «Entrar» toma el
                  color de SU brazo y el resto de Inicio se queda neutro.
                */
                <article key={brazo} className="inicio__caja" data-caja-brazo={brazo}>
                  {/*
                    ⚠️ El rótulo lleva el nombre de la instancia adelante **sólo si
                    está declarado**, con la misma regla y el mismo separador que
                    el sello (`zonas.ts` §`selloDelBrazo`).
                  */}
                  <p className="inicio__rotulo">
                    {instancia() === null
                      ? "Brazo del estudio"
                      : `${instancia() ?? ""} · Brazo del estudio`}
                  </p>
                  <h3>{selloDelBrazo(brazo)}</h3>
                  <p className="inicio__derivacion">
                    {derivacionDelBrazo(configuracion, brazo)}
                  </p>
                  {/*
                    ⛔ «Entrar» es el ÚLTIMO elemento de la caja (`CA-07`) y lleva
                    a Participantes del brazo (`CA-17`, `CA-18`), no a la raíz del
                    brazo: la raíz redirige, y una redirección de más en el
                    historial es la que `CA-85` pide que no esté.
                  */}
                  <Link
                    className="boton boton--primario inicio__entrar"
                    to={rutaDeLaPantalla(brazo, SEGMENTOS.participantes)}
                  >
                    Entrar
                  </Link>
                </article>
              ))}
            </div>
          </section>
          )}

          <section className="inicio__zona" aria-labelledby="inicio-transversales">
            <h2 id="inicio-transversales">Funcionalidades transversales</h2>
            {/* ⟦rastro · 30/9/2026⟧ ~~«aplican a los dos brazos»~~ fijo: sale de `aLosBrazos()`. */}
            <p className="inicio__nota">Son de la instancia: los cambios aplican {aLosBrazos()}.</p>
            <div className="inicio__cajas">
              {/*
                ⭐ `ADR-0679` · **la tarjeta transversal, como el boceto**
                (`Main.dc.html`): horizontal, el ícono en su cuadro blanco, el
                título con la descripción, y «Abrir» arriba a la derecha.

                ⚠️ «Abrir» sigue siendo el ÚLTIMO hijo del DOM, igual que «Entrar»
                en las cajas de brazo (`CA-07`): la posición la da la hoja, y el
                orden de lectura y de tabulación queda título → descripción →
                acción.

                ⭐ El ícono es el MISMO que el renglón del menú transversal
                (`ICONO_TRANSVERSAL`), no uno nuevo: dos dibujos para la misma
                cosa es lo que hace que el rail angosto deje de ser aprendible.
                Las rutas `#/…` que el boceto escribe debajo son anotación del
                diseñador y no se dibujan.
              */}
              <article className="inicio__caja inicio__caja--transversal">
                <span className="inicio__icono">
                  <IconoBancoDeContenido />
                </span>
                <div className="inicio__texto">
                  <h3>Banco de contenido</h3>
                  <p>Documentos con los que responde el chatbot.</p>
                </div>
                <Link className="boton inicio__entrar" to={RUTA_DEL_BANCO}>
                  Abrir
                </Link>
              </article>
              <article className="inicio__caja inicio__caja--transversal">
                <span className="inicio__icono">
                  <IconoConfiguracionDelChatbot />
                </span>
                <div className="inicio__texto">
                  <h3>Configuración del chatbot</h3>
                  <p>
                    Personalidad, guardrails, mensajes del motor, textos de derivación y auditoría
                    de cambios.
                  </p>
                </div>
                <Link className="boton inicio__entrar" to={RUTA_DE_LA_CONFIGURACION_INICIAL}>
                  Abrir
                </Link>
              </article>
            </div>
          </section>
        </main>
      </div>
    </>
  )
}

/**
 * ⛔ **«Este brazo no existe en KAI».** `R1.1-F2-CA-91`…`CA-99`.
 *
 * ⛔⛔ **Sin menú y sin tablas, y eso es criterio, no estética**: `CA-93` pide que
 * no haya menú lateral, `CA-94` que no haya tablas y `CA-98` que **no se haga
 * ningún pedido de datos de brazo**. Por eso esto NO se monta adentro del marco:
 * un marco con menú alrededor de un cartel de error sería un menú cuyos enlaces
 * relativos apuntan a un brazo que no existe.
 */
function BrazoInexistente(): JSX.Element {
  const nombre = instancia()
  const declarados = brazosDeclarados().map(nombreDelBrazo)
  return (
    <div className="error-de-zona">
      <main id="contenido">
        <h1>{nombre === null ? "Este brazo no existe" : `Este brazo no existe en ${nombre}`}</h1>
        {/*
          ⛔⛔ `[22/9/2026]` · **LA LISTA SALE DE LA DECLARACIÓN, Y ACÁ ES DONDE
          MÁS SE NOTABA.**

          ~~Antes decía `{NOMBRE_DEL_BRAZO.hibrido} y {NOMBRE_DEL_BRAZO.puro}`~~:
          la pantalla cuyo trabajo es decir **qué brazos existen** los tenía
          escritos a mano. En `OLI` —un solo brazo— este cartel enumeraba un brazo
          que la instancia no declara, o sea **mentía en el único lugar cuyo
          propósito es no mentir**.

          ⚠️ `R1.1-F2-CA-91`…`CA-99` siguen verdes sobre `KAI`, que declara los
          dos en ese orden: el texto sale idéntico —«Los brazos de KAI son: Brazo
          Híbrido y Brazo Puro.»—, sólo que ahora es una consecuencia de la
          declaración y no una copia de ella.

          ⛔ Sin brazos declarados **no se escribe la frase**: una enumeración
          vacía («Los brazos de KAI son: .») es peor que el silencio.
        */}
        {declarados.length === 1 && <p>El único brazo de {nombre ?? "esta instancia"} es: {declarados[0]}.</p>}
        {declarados.length > 1 && (
          <p>
            Los brazos de {nombre ?? "esta instancia"} son:{" "}
            {declarados.slice(0, -1).join(", ")} y {declarados[declarados.length - 1]}.
          </p>
        )}
        <Link className="boton boton--primario" to={INICIO}>
          Ir al inicio
        </Link>
      </main>
    </div>
  )
}

/**
 * ⛔ **«Esta pantalla no existe en KAI · Brazo X».** `R1.1-F2-CA-100`…`CA-105`.
 *
 * ⭐ Al revés que el anterior, **éste SÍ va adentro del marco** (`CA-100` pide
 * que el sello siga estando): el brazo existe, lo que no existe es la pantalla,
 * así que el operador no perdió la zona y el menú lo puede sacar de ahí.
 */
function PantallaInexistente({ brazo }: { readonly brazo: Brazo }): JSX.Element {
  return (
    <section className="error-de-pantalla">
      <h1>Esta pantalla no existe en {selloDelBrazo(brazo)}</h1>
      <Link className="boton boton--primario" to={rutaDeLaPantalla(brazo, SEGMENTOS.participantes)}>
        Ir a Participantes
      </Link>
    </section>
  )
}

/**
 * ⛔⛔⛔ **PUNTO DE MONTAJE PROVISORIO. NO ES LA PANTALLA, Y ESTÁ ESCRITO PARA QUE
 * NO SE PUEDA CONFUNDIR CON UNA.**
 *
 * ── DE QUIÉN ES LO QUE FALTA ───────────────────────────────────────────────
 *
 * **Dueño: `R2-UI8`, en `apps/management-ui/src/plantillas/**`.** Esta etapa
 * (`ui-marco`) puso LA DIRECCIÓN y el punto de montaje; la pantalla de «Botones
 * de respuestas automáticas» la construye aquella, que se detuvo el 23/9/2026 sin
 * escribir una línea **porque estas dos `<Route>` no existían** y sus 39 criterios
 * viven todos en direcciones que caían en «Esta pantalla no existe».
 *
 * ⛔⛔ **Por qué este provisorio se ve como un pendiente y NO como una pantalla
 * terminada.** Si dibujara una tabla vacía, un encabezado con el nombre de la
 * pestaña o un estado de carga, `R2-UI8` volvería, navegaría, vería algo que
 * arranca y **no se enteraría de que falta**. El defecto que eso produce es el
 * peor de la familia: no hay rojo, no hay error, y el operador encuentra una
 * pantalla que no hace nada. Por eso reusa `error-de-pantalla` —la misma clase
 * que los otros tres carteles de «acá no hay nada»— y dice, en la primera línea,
 * que no está construida.
 *
 * ⚠️ **Y por eso NO se sacó el `disabled` de la pestaña** (`plantillas/pantalla.tsx`,
 * territorio de `R2-UI8`): la pestaña inerte que `R2-UI1` dejó es honesta. Mandar
 * al operador desde una pantalla que funciona a este cartel sería empeorar el
 * producto, que es el argumento que `R2-UI8` midió y publicó.
 *
 * ⛔ **Este bloque se BORRA cuando la pantalla aterrice**, y lo que queda es el
 * `element=` de las dos `<Route>` de abajo apuntando a `src/plantillas/**`. Es
 * exactamente el «rebase de una línea» que `SEGMENTOS.botonesDePlantillas`
 * prometía y que hoy, por fin, es cierto.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⭐⭐⭐ **RASTRO · 23/9/2026 · LA PANTALLA ATERRIZÓ Y ESTE PROVISORIO YA NO
 * EXISTE.** `R2-UI8`, `ADR-0348`. `ADR-0082`: el encabezado no se borra, se
 * cierra.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Acá vivían `DUENO_DE_LA_PANTALLA_DE_BOTONES` y `PantallaDeBotonesPendiente()`
 * —la `<section className="error-de-pantalla" data-pendiente="R2-UI8 · …">` que
 * decía «Esta pantalla todavía no está construida»—. **Se fueron**, y el párrafo
 * de arriba dice exactamente por qué: el `element=` de las dos `<Route>` de abajo
 * ahora apunta a `src/plantillas/**`.
 *
 * ⚠️⚠️ **Y con esto queda cerrado el párrafo de más arriba que decía «por eso NO
 * se sacó el `disabled` de la pestaña»**: `R2-UI8` lo sacó, porque ya no manda al
 * operador a un cartel. La pestaña navega (`R2-F3-CA-01`) y monta la pantalla.
 *
 * ⛔⛔ **Esta edición de `App.tsx` es LA ÚNICA de `R2-UI8`, y va DECLARADA como
 * desvío**: `specs/.../r2-ui8.md` dice *«no toca `App.tsx`»* y a la vez el
 * `element=` de las dos rutas —que es lo único que puede montar la pantalla— vive
 * acá. **Las dos cosas no se pueden cumplir a la vez.** Se eligió montar la
 * pantalla, porque sin eso los 39 criterios viven en direcciones que muestran un
 * cartel de «no está construida»; el desvío está en el informe de la jornada y en
 * `ADR-0348`, y **no se tocó ningún otro renglón de este archivo**.
 */

/**
 * ⭐⭐ `R2-UI6` · **LA TERCERA PANTALLA DE «ACÁ NO HAY NADA»: “NO TIENES ACCESO A
 * ESTA PANTALLA”.** `// ADR-0353` · Regla 18 (PRD:2316), PRD:2194-2196,
 * `CA-F6-35`, `CA-F6-36`, `CA-F6-37`, `CA-F6-38`.
 *
 * ⛔⛔ **SE DIBUJA EN LA DIRECCIÓN QUE LA PERSONA ESCRIBIÓ.** PRD:2051: *«sin
 * dirección propia»*. No hay `<Route path="/acceso-denegado">` y no la puede
 * haber: `CA-F6-35` pide que escribir `#/brazos/hibrido/participantes` **muestre**
 * el cartel, y un `<Navigate>` reescribiría la barra y le contaría al operador que
 * existe una pantalla de error a la que se puede volver. Es la misma forma que
 * `PantallaInexistente`, y por eso están escritas juntas.
 *
 * ⛔ **El enlace sale de `destinoPorRol`, no de un literal.** `CA-F6-36`: *«se ve
 * el enlace “Ir a KAI · Brazo Híbrido”, y tocarlo deja la dirección en
 * `#/brazos/hibrido`»*. Y `CA-F6-55` es la razón por la que tiene que ser el rol
 * **actual**: al 403 la SPA vuelve a preguntar `quienSoy()`, así que quien llegó
 * acá porque le cambiaron el rol en la base ve la salida del rol NUEVO.
 *
 * ⚠️ **El rótulo del enlace es el SELLO de la zona de destino**, no el segmento:
 * «Ir a KAI · Brazo Híbrido» sale de `selloDelBrazo`, que es el mismo texto que el
 * rail muestra arriba. Para el Administrador —que no puede llegar acá por rol
 * (`CA-F6-41`)— el destino es Inicio y el rótulo lo dice.
 *
 * ⚠️ **Clase `error-de-pantalla` reusada a propósito**: `estilos.css` no es
 * territorio de esta etapa y una clase nueva se quedaría sin regla en la hoja
 * (`pruebas/r11-clases-sin-regla.test.ts`). Es el mismo tratamiento visual —un
 * mensaje con su salida, dentro del marco— y no se está pidiendo otro.
 */
function AccesoDenegado({ destino }: { readonly destino: string }): JSX.Element {
  /*
   * ⟲ RASTRO `[25/9/2026]` · `R2-UI19`: recibía `rol` y calculaba
   * `destinoPorRol(rol)`; ahora el destino necesita el brazo de la declaración, así
   * que lo calcula `Rutas` (que es quien lo tiene) y llega hecho. Quien no tiene
   * destino no llega acá: ve `SinBrazoDelAcompanante`.
   */
  const partes = segmentosDe(destino)
  const brazoDelDestino = partes[0] === SEGMENTOS.brazos ? partes[1] : undefined
  const rotulo =
    brazoDelDestino !== undefined && esBrazo(brazoDelDestino)
      ? `Ir a ${selloDelBrazo(brazoDelDestino)}`
      : "Ir al inicio"

  return (
    <section className="error-de-pantalla">
      <h1>No tienes acceso a esta pantalla</h1>
      <p>Esta pantalla existe, pero tu rol no la tiene.</p>
      <Link className="boton boton--primario" to={destino}>
        {rotulo}
      </Link>
    </section>
  )
}

/**
 * ⭐⭐ `R2-UI19` · **EL ACOMPAÑANTE SIN BRAZO: ACCESO DENEGADO, CON TEXTO CONSTANTE.**
 * `// ADR-0384 E`: «Si hay cero o más de uno [brazos con `derivacionHabilitada`], el
 * Acompañante no cae en ningún brazo: ve la pantalla de acceso denegado con un texto
 * constante, y **nunca** hay un brazo de repuesto escrito en el código.»
 *
 * ⛔ **Sin enlace a ningún brazo** —no hay a cuál— y **sin marco**, por lo mismo que
 * `BrazoInexistente`: un marco necesita un brazo. Lleva el bloque de sesión porque
 * es lo único que esta persona puede hacer desde acá (cerrar sesión).
 *
 * ⚠️ También es lo que se ve si `GET /configuracion` falla (red, 503, cuerpo sin
 * `brazos`): sin declaración no hay brazo, y adivinar uno es exactamente el defecto.
 * No se reintenta solo: recargar vuelve a pedir.
 */
function SinBrazoDelAcompanante(): JSX.Element {
  return (
    <div className="error-de-zona">
      <main id="contenido">
        <h1>No tienes acceso a esta pantalla</h1>
        <p>Tu rol no tiene un brazo asignado en esta instancia.</p>
        <BloqueDeSesion />
      </main>
    </div>
  )
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 * ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1` (fila 13 / B28): **BLOQUE
 * HISTÓRICO — `PantallaDelAcompanante` YA NO EXISTE.** El Acompañante entra a
 * Situaciones críticas (R4-F1), no a una espera, así que el componente que este
 * bloque documentaba se retiró. Lo de abajo se conserva redactado, no vivo
 * (`ADR-0082`); el nombre de la función que se fue queda SÓLO acá.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⭐⭐ `R2-UI6` · **LA PANTALLA DEL ACOMPAÑANTE, QUE EXPLICA POR QUÉ ESTÁ VACÍA.**
 * PRD:2166-2171, `CA-F6-31`, `CA-F6-32`.
 *
 * ⛔⛔ **`CA-F6-32`: NI UN BOTÓN NI UN ENLACE**, *«fuera del menú y de “Cerrar
 * sesión”»*. Por eso este componente no tiene ninguno — ni «Ir a Participantes»,
 * ni «Volver», ni una descarga. El único control que el Acompañante tiene en toda
 * la consola es el `<button>` del bloque de sesión, que vive en el rail.
 *
 * ⛔ **Y tampoco los renglones «No disponible» del boceto** (PRD:2478-2481,
 * `CA-F6-30`): las diferencias boceto→PRD ya dicen que no se construyen.
 *
 * ⚠️ Los dos textos son los de `CA-F6-31`, literales. Clase reusada por lo mismo
 * que `AccesoDenegado`.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ENMIENDA `R3-UI0` `[25/9/2026]` · **EL SEGUNDO TEXTO ES OTRO EN R3, Y EL
 * VIEJO SE REDACTA EN VEZ DE BORRARSE** (`ADR-0082`)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Acá decía: ~~*«Conversaciones y el panel de crisis se están construyendo.
 * Cuando estén, las vas a ver aquí.»*~~ Era el texto de R2 (`CA-F6-31`) y era
 * correcto mientras Conversaciones no existía.
 *
 * ⭐ **R3 construye Conversaciones**, así que el texto que la nombraba como
 * pendiente pasó a ser falso: lo que le falta al Acompañante es **su** pantalla.
 * `PRD_R3.md:908` (`R3-F1-CA-02`) y la regla 2 (`:855`) fijan el reemplazo,
 * literal: *«La pantalla de situaciones sensibles se está construyendo. Cuando
 * esté, la vas a ver aquí.»* — en singular, porque ahora es UNA pantalla.
 *
 * ⚠️ **El título NO cambia** (el que R2 escribió para las «pantallas»): lo piden
 * igual `CA-F6-31` y `R3-F1-CA-02`, y `CA-F6-32` (ni un botón ni un enlace) sigue
 * valiendo. ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1` (`CA-02`): de
 * `CA-F6-29`/`CA-F6-30` (el menú del Acompañante **sin renglones**) ya NO vale
 * «sin tocar nada» — el Acompañante pasa a tener un renglón, «Situaciones
 * críticas» (`rutas.ts`, `menuDelBrazo`, hoy `:367-383`); `CA-F6-30` sí sigue: los
 * «No disponible» del boceto siguen sin construirse.
 *
 * ⛔ El rastro del examen está en `pruebas/r2ui6-sesion-y-rol.test.tsx`, donde el
 * caso de `CA-F6-31` fijaba el texto viejo: se enmendó con su nota, **no se
 * borró** — borrar la aserción para que pase dejaría esta pantalla sin examen.
 */
// ⚠️ `R4-UIB1`: el rastro del retiro de `PantallaDelAcompanante` está en el
// ENCABEZADO de este bloque (`:1199-1206`). Este cierre sólo señala que el
// comentario terminó; no hay componente vivo debajo.

/**
 * ⭐ `R4-UIB1` · **el borde REAL de Situaciones críticas.**
 *
 * ⛔ Se define ACÁ y no en `src/situaciones-criticas/` porque el territorio de la
 * etapa no incluye ese directorio (el sandbox sólo monta `src/rutas.ts`,
 * `src/App.tsx` y las pruebas): `R4-UISC1` dejó la pantalla y el TIPO
 * `BordeDeSituaciones` (`pantalla.tsx:31-48`), pero ningún productor real, y sin
 * uno el `<Route>` no compila. Molde `conversaciones/borde-conversaciones.ts`.
 *
 * Las TRES rutas son las de `apps/admin-web/src/situaciones-criticas.ts:7-9`, bajo
 * el brazo que entra por el primer segmento de la dirección (`RT-4`).
 */
interface FilaDeSituacionEnLaRed {
  readonly segmento: string
  readonly fecha_hora: string
  readonly escribio: string | null
  readonly respondio: string | null
  readonly categoria: CategoriaEnLaRed | null
  /**
   * Los campos de `FilaDeCaso` (`contracts/conversation`) que el contrato pide y
   * `admin-web` HOY no manda (R4-H-001): se leen cuando llegan y, si faltan, la
   * pantalla no inventa nada.
   */
  readonly id_conversacion?: string
  readonly lineasDeAyuda?: readonly string[]
  readonly id_categoria?: string
  /** `null` en un caso anterior a R4 (`FilaDeCaso.id_participante` ausente). */
  readonly participante: {
    readonly nombre: string
    readonly telefono: string | null
    readonly lote: string | null
  } | null
  readonly atencion: { readonly nombre: string; readonly fecha_hora: string } | null
}

interface ListaDeSituacionesEnLaRed {
  readonly filas: readonly FilaDeSituacionEnLaRed[]
  readonly total: number
}

interface MensajeDeSituacionEnLaRed {
  /**
   * ⟦rastro · R4-H-008 · 7/10/2026⟧ El MENSAJE MOSTRADO, con «Plantilla: » /
   * «Button clicked: » **ya puestos por el servidor**. La pantalla no prefija.
   */
  readonly contenido: string
  readonly es_sistema: boolean
  readonly fecha_hora: string
  readonly estado_mensaje: EstadoMensajeEnLaRed
  /**
   * ⟦rastro · R4-H-008 · 7/10/2026⟧ El motivo del error de Meta **ya traducido
   * por el servidor** (`reasonFor`, un solo módulo de traducción), o `null` en la
   * fila que no muestra motivo.
   *
   * ⚠️ Va OPCIONAL en el cable, como `posicion`: un `admin-web` anterior a este
   * fix no lo manda, y la pantalla no puede quedar rota contra él. La ausencia y
   * el `null` significan lo mismo acá —«no hay motivo que mostrar»— y el `??` de
   * abajo los colapsa en el `null` que el panel espera.
   */
  readonly motivo?: string | null
}

interface ConversacionDeSituacionEnLaRed {
  readonly filas: readonly MensajeDeSituacionEnLaRed[]
  readonly total: number
  /** Posición ABSOLUTA de la fila que abrió el caso (1 = la más nueva). */
  readonly posicion?: number
  readonly pagina: number
}

interface MarcaDeSituacionEnLaRed {
  /**
   * `nombre` lo resuelve el servidor (identity) como en la lista. Va opcional: un
   * `admin-web` anterior no lo manda y entonces se cae al correo.
   */
  readonly atencion: { readonly correo: string; readonly nombre?: string | null; readonly fecha_hora: string }
}

/** El instante que `admin-web` manda YA FORMATEADO (`DD-MM-YYYY HH:mm`). */
function instanteDeSituacion(clave: string): Date {
  const m = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})$/.exec(clave)
  if (m === null) return new Date(clave)
  return new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}:00`)
}

function crearBordeDeSituacionesHttp(config: { readonly adminWebUrl: string }): BordeDeSituaciones {
  /** La raíz lleva el brazo adelante y se resuelve en cada llamada (⟦AR5-m⟧). */
  const raiz = (): string => raizDelBrazo(config.adminWebUrl)

  async function pedirJson<T>(url: string, init: RequestInit): Promise<T> {
    let respuesta: Response
    try {
      respuesta = await fetch(url, init)
    } catch (causa) {
      throw Object.assign(
        new Error(`No se pudo hablar con el servidor (${config.adminWebUrl}).`),
        { status: 0, cause: causa },
      )
    }
    if (!respuesta.ok) {
      throw Object.assign(
        new Error(await mensajeDeError(respuesta, `El servidor respondió ${respuesta.status}.`)),
        { status: respuesta.status },
      )
    }
    return (await respuesta.json()) as T
  }

  return {
    listarCasos: async ({ pagina, tamanoDePagina }) => {
      const url = new URL(`${raiz()}/situaciones-criticas`)
      url.searchParams.set("pagina", String(pagina))
      url.searchParams.set("tamano", String(tamanoDePagina))
      const cuerpo = await pedirJson<ListaDeSituacionesEnLaRed>(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      })
      const casos: readonly Caso[] = cuerpo.filas.map((fila) => {
        const atencion: Atencion =
          fila.atencion === null
            ? { estado: "sin_atender", por: null, cuando: null }
            : {
                estado: "atendido",
                por: fila.atencion.nombre,
                cuando: instanteDeSituacion(fila.atencion.fecha_hora),
                cuandoTexto: fila.atencion.fecha_hora,
              }
        return {
          // El `segmento` es el identificador que usan las TRES rutas
          // (`/situaciones-criticas/{caso}/…`): sirve de `id` y de conversación.
          id: fila.segmento,
          // El `segmento` abre las rutas; el id que se ve en la columna es el de la
          // conversación (CA-33), no el segmento. Un caso de `turn_log` no abre nada.
          id_conversacion: fila.id_conversacion ?? null,
          segmentoParaAbrir: fila.segmento.startsWith("turn_log.") ? null : fila.segmento,
          // ⟦R4-H-001 · hora de Chile⟧ el texto del servidor va TAL CUAL: ya está en hora
          // de Chile y reconstruir un `Date` lo correría con el navegador fuera de Chile.
          fecha: fila.fecha_hora,
          categoria: textoDeCategoria(fila.categoria, fila.id_categoria),
          participante:
            fila.participante === null || fila.participante === undefined
              ? null
              : {
                  nombre: fila.participante.nombre,
                  telefono: fila.participante.telefono,
                  lote: fila.participante.lote,
                },
          escribio: fila.escribio,
          respondio: fila.respondio,
          lineasDeAyuda: fila.lineasDeAyuda ?? [],
          atencion,
        }
      })
      return { casos, total: cuerpo.total }
    },

    leerConversacion: async ({ idConversacion, pagina, tamanoDePagina }) => {
      const url = new URL(
        `${raiz()}/situaciones-criticas/${encodeURIComponent(idConversacion)}/conversacion`,
      )
      url.searchParams.set("tamano", String(tamanoDePagina))
      // La página sólo viaja cuando la persona paginó (CA-60): en la apertura el
      // servidor devuelve la página de la fila que abrió el caso.
      if (pagina !== undefined) url.searchParams.set("pagina", String(pagina))
      const cuerpo = await pedirJson<ConversacionDeSituacionEnLaRed>(url.toString(), {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include",
      })
      const mensajes: readonly MensajeDeConversacion[] = cuerpo.filas.map((mensaje, indice) => ({
        id: `${cuerpo.pagina}-${indice}`,
        fecha: mensaje.fecha_hora,
        delSistema: mensaje.es_sistema,
        // ⟦rastro · R4-H-008⟧ `contenido` ya viene MOSTRADO del servidor
        // («Plantilla: …», «Button clicked: …»): acá no se prefija nada.
        texto: mensaje.contenido,
        estado: textoDeEstadoDeMensaje(mensaje.estado_mensaje),
        // ⟦rastro · R4-H-008⟧ Decía `motivo: null` FIJO, y por eso la fila
        // «Fallido» del panel salía sin motivo (`CP-R4-F1-058`). Viene del cable
        // ya traducido; la pantalla no tiene tabla de motivos (`R3-F1-CA-108`).
        motivo: mensaje.motivo ?? null,
        lineasDeAyuda: [],
      }))
      const conversacion: Conversacion = {
        id: idConversacion,
        mensajes,
        total: cuerpo.total,
        pagina: cuerpo.pagina,
        // CA-56/CA-60: el servidor manda la posición absoluta de la fila del caso;
        // acá sólo se la ubica en la página que se pidió (no se busca por texto).
        filaResaltada:
          cuerpo.posicion === undefined
            ? null
            : (mensajes[cuerpo.posicion - (cuerpo.pagina - 1) * tamanoDePagina - 1]?.id ?? null),
      }
      return conversacion
    },

    marcarAtendido: async (id) => {
      const url = `${raiz()}/situaciones-criticas/${encodeURIComponent(id)}/atencion`
      const cuerpo = await pedirJson<MarcaDeSituacionEnLaRed>(url, {
        method: "POST",
        headers: { Accept: "application/json" },
        credentials: "include",
      })
      return {
        estado: "atendido",
        // El NOMBRE de quien figura en la atención (CP-R4-F1-068/070/071); el correo
        // sólo si el servidor no lo pudo resolver.
        por: cuerpo.atencion.nombre ?? cuerpo.atencion.correo,
        cuando: instanteDeSituacion(cuerpo.atencion.fecha_hora),
        cuandoTexto: cuerpo.atencion.fecha_hora,
      }
    },
  }
}

/**
 * ⭐ `CP-F4-010` · «Pidió humano» es **solo Híbrido**, y acá se decide si va.
 *
 * El defecto: la columna se dibujaba en el brazo `puro`. El PRD es inequívoco
 * —`docs/prd/F4.md:45` *«Pidió humano (solo Híbrido)»*, `F8.md:60` *«`pidio_humano`
 * (solo Híbrido): `true` si presiona “pásame con una persona”»*— y el botón que
 * la produce **no existe en el brazo puro**: ahí la columna no vale cero, no
 * tiene sentido.
 *
 * **Este componente existe porque la decisión necesita el brazo de la RUTA**, y
 * el brazo sólo se puede leer adentro de un componente montado por el enrutador:
 * `pantallas()` se evalúa en el render de `App`, antes de que exista la ruta.
 *
 * ⛔⛔ RASTRO `[17/9/2026]` — acá decía que la regla tenía **dos entradas**:
 *
 *   ~~«si la instancia NO declaró `brazoHibrido`, o la ruta NO declaró brazo ⇒ se
 *   muestra […] Nunca se oculta por adivinanza. Sin un default de `brazoHibrido`,
 *   la peor falla posible es seguir mostrando de más —visible— en vez de esconder
 *   una métrica en silencio en una instancia que llamó a sus brazos de otra
 *   forma.»~~
 *
 * El razonamiento era correcto para su mundo y **la falla que describía ocurrió**:
 * la `configuracion.json` de QA no declara `brazoHibrido`, así que «se muestra»
 * ganó siempre y el Puro dibujó una columna que su propio texto de ayuda declara
 * «solo Híbrido». *Visible*, sí — durante meses y sin que nadie la viera.
 *
 * ⭐ Lo deroga `PRD_R1.1.md:204`: *«Solo existen los brazos `hibrido` y `puro`;
 * cualquier otro valor se rechaza con error»*. **Ya no hay adivinanza que evitar**:
 * el conjunto es cerrado, vive en `zonas.ts` y la ruta lo nombra. La entrada es
 * una sola.
 */
function MetricasDelBrazo(): JSX.Element {
  const brazo = useBrazo()
  /*
   * ⛔⛔ ENMIENDA `R1.1-F1-CA-53` / `R1.1-F4-CA-94` · **el brazo sale de la RUTA y
   * ya no de `config.brazoHibrido`.**
   *
   * ~~Antes la decisión pedía dos entradas —el brazo de la ruta y el nombre que la
   * instancia le daba al híbrido— y si `brazoHibrido` no estaba declarada, `decidible`
   * era `false` y la columna se mostraba en los dos brazos.~~ **Ése era el defecto
   * medido**: la `configuracion.json` de QA no declara `brazoHibrido`, así que el
   * Puro dibujaba una columna cuyo propio texto de ayuda dice «solo Híbrido».
   *
   * ⭐ Lo deroga `PRD_R1.1.md:204`: *«Solo existen los brazos `hibrido` y `puro`;
   * cualquier otro valor se rechaza con error»*. En esta consola el conjunto es
   * CERRADO y vive en `zonas.ts`, así que no hay nada que adivinar: si la ruta
   * dice `puro`, es el Puro. La cautela vieja —no esconder por adivinanza— ya no
   * protege de nada, porque ya no hay adivinanza.
   */
  /*
   * ⭐ `R2-UI17` (`R2-H-048`) · `key={brazo}`, la misma llave que `EnviosDelBrazo`:
   * cambiar el brazo en la dirección es otra pantalla, no la misma con otro sello.
   * Ver `PantallaDelBrazo`, que tiene el motivo entero.
   */
  return <PantallaMetricas key={brazo ?? ""} {...(esBrazo(brazo) ? { brazo } : {})} />
}

/**
 * ⭐⭐ `AW46` · **El catálogo del brazo declarado por la RUTA, para los dos
 * selectores.**
 *
 * ⛔⛔ **Este hook existe porque el brazo se resuelve ADENTRO de la ruta y los
 * bordes se arman AFUERA.** `App()` construye `bordePlantillas` en el render de
 * la raíz —la dirección de `admin-web` llega en tiempo de ejecución— y ahí
 * `useBrazo()` todavía no puede contestar: el parámetro de ruta sólo existe
 * dentro del `<Route>`. Es el mismo patrón que `MetricasDelBrazo` ya usa, y por
 * el mismo motivo.
 *
 * ⭐ **`null` ⇒ la fuente VACÍA, no un brazo por defecto.** Ver `./brazo.ts`:
 * `Arm = string` y los brazos son `[CONFIG]` por instancia, así que cualquier
 * default sería el nombre de una instancia disfrazado de constante — y el
 * catálogo del brazo equivocado, ofrecido en un selector, es una plantilla del
 * otro brazo elegida por el operador y un envío que falla del lado del proveedor
 * mensaje por mensaje. **Falla cerrada, y el selector lo dice.**
 *
 * ⚠️ **De dónde SALE el brazo en estas pantallas es la costura abierta (c) de
 * `AW46` y no se decide acá**: se usa el mecanismo que este árbol ya tiene
 * (`ADR-0136` ②, el brazo en la ruta). Las dos lecturas candidatas —la sesión
 * autenticada, que depende de `ADR-0006` y hoy no existe, o un selector explícito
 * en la pantalla, que ninguna fuente autoriza— **tienen dueño: el TL con
 * `apps-integrator`**.
 *
 * `useMemo` y acá **no es micro-optimización, es correctitud**: la fuente es
 * dependencia del `useEffect` que lee el catálogo, y una función nueva por render
 * volvería a pedirlo en cada render.
 */
function useCargarPlantillas(borde: BordePlantillas): CargarPlantillas {
  const brazo = useBrazo()
  return useMemo<CargarPlantillas>(
    () =>
      brazo === null
        ? SIN_CATALOGO_DE_PLANTILLAS
        : () => borde.catalogo(brazo).then((catalogo) => catalogo.templates),
    [borde, brazo],
  )
}

/**
 * ⭐ `R2-UI11` · Las dos fuentes de «Nuevo envío» cuando la dirección NO trae
 * brazo. **Rechazan nombrando la causa**, y no resuelven una fuente vacía:
 *
 *   · una `ListaParaEnvios` vacía armada a mano exigiría inventar su
 *     `lastAttempt`, que es un hecho del brazo (`envios/formulario.tsx`,
 *     docstring de `listaParaEnvios`);
 *   · el formulario ya dibuja el rechazo de la lista como «No se pudo leer la
 *     lista de plantillas para envíos del brazo. {causa}» y no ofrece nada. Falla
 *     cerrado y lo dice (`./brazo.ts`: «decir por qué, no adivinar»).
 *
 * ⛔ Nunca un brazo por defecto: ver `useCargarPlantillas`, que tiene el motivo.
 */
const CAUSA_SIN_BRAZO =
  "La dirección no dice de qué brazo es este envío, así que no se pidió nada."
const SIN_BRAZO_PARA_LA_LISTA: CargarListaParaEnvios = () =>
  Promise.reject(new Error(CAUSA_SIN_BRAZO))
const SIN_BRAZO_PARA_LOS_LOTES: () => Promise<readonly string[]> = () =>
  Promise.reject(new Error(CAUSA_SIN_BRAZO))

/**
 * ⭐⭐ `R2-UI11` · La lista «Plantillas para envíos» del brazo de la DIRECCIÓN,
 * para el selector de «Nuevo envío». Hermano de `useCargarPlantillas`, y por los
 * mismos dos motivos:
 *
 *   · **el brazo se resuelve ADENTRO de la ruta** (`useBrazo()`), porque
 *     `bordePlantillas` se arma en la raíz, donde el parámetro todavía no existe;
 *   · **`useMemo` sobre `[borde, brazo]` es correctitud**: la función es
 *     dependencia del `useEffect` del formulario que pide la lista, y una nueva
 *     por render la pediría en cada render.
 *
 * `null` ⇒ `SIN_BRAZO_PARA_LA_LISTA`, que rechaza. Nunca la lista de otro brazo.
 */
function useListaParaEnvios(borde: BordePlantillas): CargarListaParaEnvios {
  const brazo = useBrazo()
  return useMemo<CargarListaParaEnvios>(
    () => (brazo === null ? SIN_BRAZO_PARA_LA_LISTA : () => borde.listaParaEnvios(brazo)),
    [borde, brazo],
  )
}

/**
 * ⭐⭐ `R2-UI11` · Los lotes del brazo para el filtro «Filtrar por lote».
 *
 * ⛔ **No es un `fetch` nuevo**: envuelve `bordePadron.lotes`, que es
 * `bordeExportacion.lotes` —la MISMA función que ya usan Participantes y
 * Exportación—. Un segundo literal de `exportaciones/lotes` es el modo de falla
 * que `padron/borde-padron.ts` dejó escrito.
 *
 * ⚠️ **Por qué se envuelve en vez de pasarla derecho, y es lo que no se ve en un
 * montaje fresco:** esa función lee el brazo de la DIRECCIÓN en el momento de
 * llamarla, y su IDENTIDAD no cambia con el brazo (se memoiza por `config`).
 * Pasada derecho, ir de «Nuevo envío» del Híbrido al del Puro sin desmontar no
 * vuelve a disparar el `useEffect` del formulario que la usa, y el filtro del Puro
 * seguiría ofreciendo los lotes del Híbrido. La envoltura memoizada sobre
 * `[lotes, brazo]` cambia de identidad exactamente cuando cambia el brazo.
 *
 * `[medido 24/9/2026, pruebas/r2-ui11-vinculo-de-nuevo-envio.test.tsx]` Hoy hay
 * DOS defensas para ese caso, y es a propósito: esta envoltura y el
 * `key={brazo}` de `EnviosDelBrazo`. Quitando las dos, el caso ⑤ se pone rojo;
 * quitando una sola, no. La llave sola no alcanza para lo que el formulario
 * pide de sus fuentes (que cambien con el brazo, guard 2 de `R2-UI11`), y la
 * envoltura sola no alcanza para el caso ⑥.
 */
function useLotesDelBrazo(
  lotes: () => Promise<readonly string[]>,
): () => Promise<readonly string[]> {
  const brazo = useBrazo()
  return useMemo<() => Promise<readonly string[]>>(
    () => (brazo === null ? SIN_BRAZO_PARA_LOS_LOTES : () => lotes()),
    [lotes, brazo],
  )
}

/**
 * ⭐⭐ `R2-UI17` (hallazgo `R2-H-048`) · **Otro brazo es otra pantalla**, para las
 * que no necesitan que el envoltorio les arme nada más.
 *
 * `[medido 24/9/2026, gates/cambio-de-brazo-por-la-direccion.spec.ts]` Participantes,
 * Conversaciones y Exportación montaban la pantalla directo en el `<Route>`: al
 * cambiar `#/brazos/hibrido/…` por `#/brazos/puro/…` en la barra de direcciones
 * el marco cambiaba el sello, pero la pantalla seguía montada con lo ya cargado
 * y no volvía a pedir su fuente (el brazo lo leen sus bordes al LLAMAR, y el
 * `useEffect` que llama no dependía del brazo). Exportación ofrecía los lotes del
 * Híbrido bajo el sello «Brazo Puro». Métricas tenía el mismo defecto con su
 * envoltorio propio (`MetricasDelBrazo`, que lleva la misma llave).
 *
 * La defensa es la de `EnviosDelBrazo` desde `R2-UI11`: `key={brazo}`. Un
 * `useEffect` que sólo volviera a pedir la lista no limpiaría el resto del estado
 * (filtros, selección, paginado, lo ya dibujado); la llave desmonta todo de una
 * vez y en un solo lugar, sin tocar ninguna de las pantallas.
 *
 * `null` (sin brazo en la dirección) también es una llave: la pantalla hace lo
 * que ya hacía sin brazo. Nunca un brazo por defecto.
 */
function PantallaDelBrazo({ children }: { readonly children: ReactNode }): JSX.Element {
  const brazo = useBrazo()
  return <Fragment key={brazo ?? ""}>{children}</Fragment>
}

/**
 * `AW46` · La pantalla «Envíos» con el catálogo de plantillas de SU brazo.
 *
 * Envoltorio mínimo por lo mismo que `MetricasDelBrazo`: el brazo se lee adentro
 * de la ruta. ~~No agrega ninguna decisión propia.~~
 *
 * ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11` (hallazgo `R2-H-023`).** Desde `R2-UI2`
 * «Nuevo envío» necesitaba dos fuentes más —la lista «Plantillas para envíos» y
 * los lotes del brazo— y este envoltorio, que es el único que conoce el brazo de
 * la ruta, no se las pasaba: el selector quedaba vacío y el filtro de lotes no se
 * dibujaba, con `tsc` en cero porque las dos eran opcionales. Ahora las arma
 * (`useListaParaEnvios`, `useLotesDelBrazo`) y son obligatorias en
 * `PantallaEnvios`, así que olvidarlas es un error de compilación.
 *
 * Y agrega UNA decisión propia: `key={brazo}`. Cambiar de brazo sin desmontar
 * (Nuevo envío del Híbrido → del Puro) dejaba vivo el estado del formulario —la
 * plantilla elegida, las filas de la lista si el pedido del Puro fallaba, la
 * selección de participantes— y eso es ofrecer, o incluso crear, un envío del
 * Puro con cosas del Híbrido. Con la llave, otro brazo es otro formulario.
 */
function EnviosDelBrazo({
  cargarElegibles,
  bordePlantillas,
  lotesDelBrazo,
  abrirFormulario = false,
}: {
  readonly cargarElegibles: ReturnType<typeof crearCargarElegiblesHttp>
  readonly bordePlantillas: BordePlantillas
  /**
   * ⭐ `R2-UI11` · `bordePadron.lotes`, que llega a `pantallas()` desde la raíz.
   * Ver `useLotesDelBrazo`.
   */
  readonly lotesDelBrazo: ReturnType<typeof crearBordePadronHttp>["lotes"]
  /**
   * ⭐ `R1.1-F4-CA-78` · lo pone la `<Route>` de `envios/nuevo` y nada más. Quien
   * sabe la dirección es esta tabla de rutas; la pantalla no la lee (ver
   * `envios/pantalla.tsx` §`abrirFormulario`, con el rojo que lo decidió).
   */
  readonly abrirFormulario?: boolean
}): JSX.Element {
  const cargarPlantillas = useCargarPlantillas(bordePlantillas)
  const listaParaEnvios = useListaParaEnvios(bordePlantillas)
  const lotesDe = useLotesDelBrazo(lotesDelBrazo)
  const brazo = useBrazo()
  return (
    <PantallaEnvios
      key={brazo ?? ""}
      cargarElegibles={cargarElegibles}
      cargarPlantillas={cargarPlantillas}
      listaParaEnvios={listaParaEnvios}
      lotesDe={lotesDe}
      abrirFormulario={abrirFormulario}
    />
  )
}

/**
 * `AW46` · El simulador con el catálogo de plantillas de SU brazo. Mismo
 * envoltorio y mismo motivo que `EnviosDelBrazo`.
 */
function SimuladorDelBrazo({
  borde,
  bordePadron,
  bordeConversaciones,
  bordeEnvios,
  bordePlantillas,
}: {
  readonly borde: BordeSimulador
  readonly bordePadron: ReturnType<typeof crearBordePadronHttp>
  readonly bordeConversaciones: ReturnType<typeof crearBordeConversacionesHttp>
  readonly bordeEnvios: ReturnType<typeof crearBordeEnviosHttp>
  readonly bordePlantillas: BordePlantillas
}): JSX.Element {
  const cargarPlantillas = useCargarPlantillas(bordePlantillas)
  return (
    <PantallaSimulador
      borde={borde}
      bordePadron={bordePadron}
      bordeConversaciones={bordeConversaciones}
      bordeEnvios={bordeEnvios}
      cargarPlantillas={cargarPlantillas}
    />
  )
}

/**
 * Las rutas de las pantallas, declaradas UNA vez.
 *
 * Es una función que devuelve un fragmento y no un componente porque `<Routes>`
 * sólo mira hijos `<Route>`: un componente que devolviera este bloque no lo vería
 * el enrutador. Devolverlo así deja montar el mismo juego de pantallas bajo más
 * de un prefijo sin duplicar la lista.
 */
/**
 * `B32` / `CA-110` · El alta, con el brazo de la ruta como LLAVE.
 *
 * `useBrazo()` sólo contesta ADENTRO del `<Route>` (`brazo.ts`), y `pantallas`
 * devuelve los `<Route>` desde afuera de la coincidencia: por eso la llave no se
 * puede escribir en el mismo renglón del `<Route>`. Con el brazo como `key`,
 * cambiar de brazo DESMONTA el alta y la respuesta en vuelo del brazo anterior no
 * pinta la pantalla nueva.
 */
function AltaDelBrazo(): JSX.Element {
  const brazo = useBrazo()
  return (
    <PantallaDelBrazo key={brazo ?? ""}>
      <PantallaAlta />
    </PantallaDelBrazo>
  )
}

/** Lo mismo para la carga en lote interna, que también es escritura (`B32`). */
function CargaEnLoteDelBrazo({
  borde,
}: {
  readonly borde: ReturnType<typeof crearBordeCargaHttp>
}): JSX.Element {
  const brazo = useBrazo()
  return (
    <PantallaDelBrazo key={brazo ?? ""}>
      <PantallaCargaEnLote borde={borde} />
    </PantallaDelBrazo>
  )
}

function pantallas(
  cargarElegibles: ReturnType<typeof crearCargarElegiblesHttp>,
  bordePadron: ReturnType<typeof crearBordePadronHttp>,
  bordeCarga: ReturnType<typeof crearBordeCargaHttp>,
  bordeSimulador: BordeSimulador | null,
  /**
   * ⭐ `AW24` · El borde de envíos, para la columna de plantilla del simulador.
   * Va por PARÁMETRO y no por un contexto nuevo: el bloque ③ del encabezado ya
   * dejó escrito que `gates/pantalla-envios.spec.ts` DESCUBRE los contextos
   * barriendo `src/contexto-*.tsx`, así que un contexto nuevo cambiaría lo que ese
   * arnés monta. Un parámetro no toca nada de eso.
   */
  bordeEnvios: ReturnType<typeof crearBordeEnviosHttp>,
  /**
   * ⭐⭐ `AW28` · El borde de CONVERSACIONES, para que el simulador muestre la sesión
   * más reciente del participante. **Es el MISMO que arma la tabla de conversaciones**
   * y no uno nuevo: un segundo cable sería una segunda declaración de los mismos
   * parámetros de query, y el día que `admin-web` renombre uno la tabla se rompe y el
   * simulador sigue compilando y deja de andar.
   *
   * ⚠️ Va por PARÁMETRO y no por el contexto que ya existe, por el mismo motivo que
   * `bordeEnvios`: los bordes del simulador entran por prop para que un examen los
   * pueda reemplazar por un doble sin montar el proveedor.
   */
  bordeConversaciones: ReturnType<typeof crearBordeConversacionesHttp>,
  /**
   * ⭐⭐ `AW46` · El borde del CATÁLOGO DE PLANTILLAS. Va por PARÁMETRO y no por un
   * contexto nuevo, por el mismo motivo que los otros cinco: el bloque ③ del
   * encabezado deja escrito que `gates/pantalla-envios.spec.ts` DESCUBRE los
   * contextos barriendo `src/contexto-*.tsx`, así que un contexto nuevo cambiaría
   * lo que ese arnés monta.
   */
  bordePlantillas: BordePlantillas,
  /**
   * ⭐ `R4-UIB1` · el borde de Situaciones críticas. Va por PARÁMETRO por el mismo
   * motivo que los otros seis: que la pantalla se pueda ejercer contra un doble sin
   * montar ningún proveedor. Es a la vez el que usa el Administrador (`CA-11`) y el
   * Acompañante (`CA-01`).
   */
  bordeSituaciones: BordeDeSituaciones,
  /*
   * ⛔⛔ ENMIENDA `[17/9/2026]` · ~~`brazos: readonly string[] | undefined`~~ — el
   * parámetro se va de esta función porque **no le queda ningún consumidor**.
   *
   * Lo tenían dos: el `<select>` del alta manual (`AP40`) y el filtro «Todas» del
   * menú de plantillas (`ADR-0249`). `PRD_R1.1.md:670` y `:44` sacan los dos, y
   * `:206` dice de dónde sale el brazo ahora: de la dirección.
   *
   * ⚠️ **`ConfiguracionInstancia.brazos` NO se toca**: sigue declarado en
   * `configuracion.ts`, sigue parseado y sigue siendo `[CONFIG]` por instancia.
   * Lo que se retira es su uso en estas dos pantallas, no el campo. Que quede sin
   * consumidores en `management-ui` va reportado: retirarlo es una decisión de
   * configuración de instancia, no de una pantalla.
   */
): ReactNode {
  return (
    <>
      {/*
        ⭐ `R1.1-F2-CA-87` · Entrar a un brazo sin pantalla es entrar a
        Participantes.

        ── LO QUE ESTA LÍNEA DECÍA (no se borra) ──────────────────────────────
        ~~«Entrar al marco es entrar al Banco de Contenido.»~~ ⇒ el banco dejó de
        ser una pantalla del brazo: es TRANSVERSAL (`PRD_R1.1.md:468`,
        `R1.1-F2-CA-20`) y su `<Route>` está en `Rutas`, bajo `#/instancia/`.
      */}
      <Route index element={<Navigate to={SEGMENTOS.participantes} replace />} />
      {/*
        ⭐ `AW15` · Participantes es EL PADRÓN — y acá se levanta la parada de
        `TR-003` que este bloque dejó escrita el 22/8.

        **La parada era correcta y no se borra: se resuelve.** Lo que decía,
        textual, era que «construir “Participantes” sobre `GET elegibles` daría
        una pantalla que SE PARECE al CRUD del padrón y no lo es», porque:

          · `apps/admin-web/src/rutas.ts` tenía CUATRO rutas de participantes
            —`POST participantes` (ingesta), `GET participantes/reporte/{id}`,
            `POST participantes/alta`, `PATCH participantes/{id}/flag`— y
            **ninguna listaba el padrón**;
          · la única lectura paginada de personas era `paginaDeElegibles` ⇒
            `GET elegibles`, que es la vista ELEGIBLE (`opt_out = false`) y
            proyecta TRES campos —`id`/`name`/`phone`— sin los flags, así que un
            participante al que se le marcara `opt_out` DESAPARECERÍA de la
            lista sin poder volver;
          · `master` y `deLotes` devolvían el padrón entero sin paginar y no
            estaban expuestas por HTTP.

        **Lo que la levanta es que ahora existe el borde que faltaba**, y son
        tres piezas que aterrizaron después: `paginaDeMaster` con `telefono` y
        `nombre` en `contracts/participants` (`CP20`), `MasterListItem` —los OCHO
        campos de `ADR-0191`— y `listMaster` en `contracts/read-api` (`CR5`), y
        la ruta `GET padron` en `apps/admin-web` (`AP28`). La quinta ruta de
        participantes es la que lista el padrón, y es la que esta pantalla
        consume.

        ⛔ **Esta ruta NO trae el seteo de flags por fila**: eso es `AW15b`, con
        su propio `acceptance` y su propio guard sobre el desenlace de un flag
        que falla. Es una ESCRITURA sobre datos de menores y no se cuela adentro
        de una pantalla de lectura.
      */}
      {/* ⭐ `R2-UI17` · `PantallaDelBrazo`: otro brazo en la dirección, otra pantalla. */}
      <Route
        path={SEGMENTOS.participantes}
        element={
          <PantallaDelBrazo>
            <TablaPadron borde={bordePadron} />
          </PantallaDelBrazo>
        }
      />
      {/*
        `AW7` · el alta manual (`P0-5`), que hasta `AW15` vivía en
        `participantes`. Sigue siendo la MITAD DE ESCRITURA y nada más —`enroll`
        y `setFlag` sobre lo que la sesión dio de alta—; lo que cambió es que ya
        no es lo ÚNICO que hay bajo «Participantes». Ver `SEGMENTOS.alta`.
      */}
      {/*
        ⭐⭐ `AP40` `[9/9/2026]` · `brazos` entra ACÁ TAMBIÉN, y es la segunda
        pantalla que lo recibe.

        Hasta hoy este `<Route>` montaba `<PantallaAlta />` **sin props**, y el
        `<select>` de brazo tenía los dos nombres escritos a mano — el hallazgo
        `H-AUD-009`, que `R1-F1 §6` prohíbe con todas las letras («ni de una
        constante en código»). El campo que faltaba ya existía desde el 2/9: es el
        mismo `config.brazos` que la línea de abajo le pasa a plantillas, y el
        mismo parámetro `brazos` de esta función. **No hay plomería nueva.**

        Mismo `{...(… ? { brazos } : {})}` que `plantillas`, por el mismo motivo:
        `exactOptionalPropertyTypes` hace que `brazos={undefined}` y la prop
        ausente NO sean lo mismo.

        ⚠️ Ausente ⇒ la pantalla **dice que no puede dar de alta**, y no se
        comporta «como antes»: acá el brazo es obligatorio para el pedido, así que
        no hay un «como antes» al que caer. Ver `alta.tsx`
        ⟦el-brazo-sale-de-la-declaracion⟧.
      */}
      {/*
        ⛔ ENMIENDA `R1.1-F4-CA-19` · ~~`<PantallaAlta {...(brazos !== undefined ? {
        brazos } : {})} />`~~ — el alta deja de recibir la lista de brazos, porque
        deja de tener campo de brazo (`PRD_R1.1.md:670`). El brazo lo lee de la
        ruta, como el padrón. Ver `alta.tsx`, que tiene el rastro entero de `AP40`.
      */}
      <Route path={SEGMENTOS.alta} element={<AltaDelBrazo />} />
      {/*
        ⭐ `AW17` · la carga en lote INTERNA del operador (`ADR-0195`).

        ⛔ **La ruta existe; la contención de `ADR-0195` §Alcance —«no se le
        entrega, ni se le documenta, ni se le ofrece a Elige»— es PROSA y este
        `<Route>` no la hace cumplir.** El propio ADR lo declara así, y esta
        tabla no finge lo contrario: cualquiera que entre al Management Tool
        llega a esta pantalla. Ver el encabezado de `carga-en-lote/pantalla-carga.tsx`
        y `Q17`, que es su condición de vencimiento, con dueño el TL.

        El borde va por PROP, como `bordePadron` y `cargarElegibles`: este
        archivo ya dejó escrito (bloque ③) que `gates/pantalla-envios.spec.ts`
        DESCUBRE los contextos barriendo `src/contexto-*.tsx`, así que un
        contexto nuevo cambia lo que ese arnés monta.
      */}
      <Route path={SEGMENTOS.cargaEnLote} element={<CargaEnLoteDelBrazo borde={bordeCarga} />} />
      {/* `AW4` · la pantalla «Conversaciones»: la tabla de solo lectura. */}
      {/* ⭐ `R2-UI17` · `PantallaDelBrazo`: otro brazo en la dirección, otra pantalla. */}
      <Route
        path={SEGMENTOS.conversaciones}
        element={
          <PantallaDelBrazo>
            <TablaConversaciones />
          </PantallaDelBrazo>
        }
      />
      {/*
       * ⭐ `R4-UIB1` · `CA-01`/`CA-11`: el marco de Situaciones críticas. El
       * Acompañante cae acá (su `destinoPorRol`); el Administrador lo alcanza por
       * el renglón que `MENU_DEL_BRAZO` le dio tras «Conversaciones». El borde
       * entra por PARÁMETRO, igual que los otros seis.
       */}
      <Route
        path={SEGMENTOS.situacionesCriticas}
        element={<PantallaDeSituaciones borde={bordeSituaciones} />}
      />
      {/*
        ⭐⭐ `AW48` · la pantalla de configuración del chatbot: Personalidad,
        Guardrails, Mensajes del motor y Derivación (`R1-F2 §4`).

        El borde entra por CONTEXTO y no por prop, igual que los de exportación,
        métricas, envíos y conversaciones: la pantalla está a varios niveles de la
        raíz y la alternativa es plomería por props a través del montaje. Lo que
        eso compra de verdad es que la pantalla se pueda ejercer contra un doble en
        memoria, sin red y sin `apps/admin-web` — que hoy está construyendo estas
        seis rutas en paralelo, contra el mismo contrato.
      */}
      {/*
        ⛔ `R1.1-F3` · **LA CONFIGURACIÓN DEL CHATBOT SALIÓ DE ACÁ.** Su `<Route>`
        vive en `Rutas`, bajo `#/instancia/configuracion-del-chatbot/*`, y el
        `/*` no es un detalle: `configuracion-del-chatbot/rutas.ts` dejó pedido
        por escrito que *«el marco tiene que servir
        `instancia/configuracion-del-chatbot/*`, no sólo el prefijo exacto. Con
        la ruta de hoy —coincidencia EXACTA— las cinco direcciones […] no las
        sirve nadie»*. El comentario de acá abajo sobre por qué su borde entra
        por CONTEXTO sigue vigente y se mudó con la ruta.
      */}
      {/* `AW10`/`AW14` · la pantalla «Envíos»: la tabla y el formulario «Nuevo envío». */}
      <Route
        path={SEGMENTOS.envios}
        element={
          <EnviosDelBrazo
            cargarElegibles={cargarElegibles}
            bordePlantillas={bordePlantillas}
            lotesDelBrazo={bordePadron.lotes}
          />
        }
      />
      {/*
        ⭐ `R1.1-F2-CA-28`/`CA-38`/`CA-48`/`CA-58` y `PRD_R1.1.md:594` ·
        **«Nuevo envío» gana DIRECCIÓN PROPIA**, `#/brazos/<brazo>/envios/nuevo`.
        El menú del brazo la nombra como subítem de Envíos.

        ~~⚠️⚠️ **HUECO DECLARADO, Y NO SE TAPA DESDE ACÁ.** La ruta existe y monta
        `PantallaEnvios`, que **hoy dibuja la lista y el formulario juntos**: la
        dirección lleva a la pantalla correcta, pero no la abre EN el formulario.
        Partir esa pantalla es trabajo de `AW10`/`AW14` con su propio examen, y
        hacerlo desde acá sería decidir por otra etapa sobre una pantalla que hoy
        pasa sus casos. ⇒ `CA-28` (el menú lleva a la dirección) queda cumplido;
        el criterio de F5 sobre el marco del boceto de Nuevo envío, NO. Reportado.~~

        ⛔⭐ **RASTRO · 24/9/2026, `R2-UI11`.** El hueco de arriba lo cerró
        `R2-UI2` (`6fa9bbf66`): esta dirección pasa `abrirFormulario` y la pantalla
        abre SÓLO el formulario, como pantalla propia. Lo que `R2-UI2` dejó abierto
        —y no declaró— fue el CABLEADO DE DATOS: esta ruta no le pasaba a «Nuevo
        envío» ni la lista «Plantillas para envíos» ni los lotes del brazo
        (hallazgo `R2-H-023`). Desde `R2-UI11` los pasa `EnviosDelBrazo`, y las dos
        `<Route>` le entregan `bordePadron.lotes`.
      */}
      <Route
        path={SEGMENTOS.nuevoEnvio}
        element={
          <EnviosDelBrazo
            cargarElegibles={cargarElegibles}
            bordePlantillas={bordePlantillas}
            lotesDelBrazo={bordePadron.lotes}
            abrirFormulario
          />
        }
      />
      {/*
        ⭐⭐ `AW46` · el menú de plantillas del brazo: qué plantillas tiene, con el
        estado de aprobación **tal como Meta lo nombra**, **cuándo se sincronizó la
        lista** —que es otra cosa que estar aprobada— y **los botones de cada una en
        su orden**, más el botón de sincronización manual que el PRD pide textual
        dos veces (L323-324 y L786-787, «en el Management Tool»).

        ⛔ **Vive acá y no en `apps/admin-web`**, y no es una preferencia: el PRD
        nombra el lugar, `admin-web` **no autentica a nadie** (`ADR-0221`) así que
        poner el botón ahí lo pondría detrás de una puerta que no es una puerta, y
        `web-dev` tiene prohibido escribir ese árbol.

        ⛔⭐ **RASTRO `[2/9/2026]`** — acá decía *«⚠️ Sin renglón en el menú, por
        el hueco declarado en `SEGMENTOS.plantillas`»*, y **dejó de ser cierto**:
        el renglón existe desde `4fbda2fe`. Se deja el rastro porque quien llegue
        con la creencia vieja tiene que encontrar acá qué la levantó.

        ⭐ `brazos` es lo que vuelve a ésta la primera pantalla que no es
        por-brazo (`ADR-0249`). Ausente ⇒ se comporta como antes, un brazo, el de
        la ruta. ~~Es lo ÚNICO que esta pantalla recibe y ninguna otra~~ — dejó de
        ser cierto el `[9/9/2026]` con `AP40`: el alta manual lo recibe también, y
        **allá `ausente` NO se comporta como antes**, porque el brazo es
        obligatorio para el pedido. Dos consumidores, dos conductas para el mismo
        `undefined`, y cada una escrita en su pantalla.

        ⛔ **El `{...(… ? { brazos } : {})}` no es un adorno**:
        `exactOptionalPropertyTypes` está prendido, así que `brazos={undefined}` y
        **la prop ausente no son lo mismo** — pasar el primero rompería el tipo.
        Es el mismo truco que `configuracion.ts` usa para no agregar la clave.

        ⚠️ **Y lo que esto deja abierto, dicho acá porque el dueño de esta línea
        es el dueño del marco:** con «Todas» elegidas, el sello del marco sigue
        diciendo el brazo de la RUTA. El selector no navega —a propósito, así la
        URL sigue significando lo mismo— así que el sello **no puede** saber la
        elección. Ver el hueco declarado en `Marco`.
      */}
      {/*
        ⛔ ENMIENDA `R1.1-F4-CA-55`/`CA-56` · ~~`{...(brazos !== undefined ? {
        brazos } : {})}`~~ — Plantillas deja de recibir la lista de brazos, porque
        deja de tener filtro y columna de brazo (`PRD_R1.1.md:44`). El brazo lo lee
        de la ruta, como el padrón, el alta y la carga.

        ⭐ **Y con eso se cierra el hueco que `Marco` dejó declarado** —«con “Todas”
        elegidas, el sello dice el brazo de la ruta sobre una tabla que muestra
        varios»—: ya no hay «Todas», así que el sello y la tabla dicen lo mismo.
      */}
      <Route path={SEGMENTOS.plantillas} element={<PantallaPlantillas borde={bordePlantillas} />} />
      {/*
        ⭐⭐ `R2-UI1` · **Costura 2: «Agregar plantilla» tiene dirección propia.**
        `R2-F2-CA-17` / `CA-19` (`PRD_R2.md:824`, `:865`).

        ⛔ **Son dos `<Route>` HERMANAS con el mismo elemento y una prop**, y no un
        `path="plantillas/*"` con rutas anidadas. El motivo es `rutas.ts`
        §`PANTALLAS_DEL_BRAZO`: esa lista es **cerrada** y se compara literal, y un
        comodín acá aceptaría `…/plantillas/cualquier-cosa` montando la pantalla
        mientras la lista dice que no existe — o sea, las dos fuentes dirían cosas
        distintas sobre la misma dirección, que es exactamente lo que el comentario
        de aquella lista advierte que `tsc` no puede sostener.

        ⚠️ **La prop es lo que hace que la dirección sobreviva a la recarga**
        (`CA-19`): el panel se dibuja porque la URL lo dice, no porque alguien haya
        tocado un botón en esta sesión.
      */}
      <Route
        path={SEGMENTOS.agregarPlantilla}
        element={<PantallaPlantillas borde={bordePlantillas} panelAgregar />}
      />
      {/*
        ⭐⭐⭐ ⟦la-ruta-de-botones⟧ · `ui-marco` · **LA MITAD QUE `R2-UI6` NO
        ESCRIBIÓ, Y QUE DETUVO A `R2-UI8`.**

        `rutas.ts` tenía su mitad desde el 22/9 —`SEGMENTOS.botonesDePlantillas` en
        `SEGMENTOS` y en `PANTALLAS_DEL_BRAZO`— y **acá no había nada**. Medido con
        una sonda sobre `<App/>` entero, sesión de Administrador, 23/9/2026:

          · `#/brazos/hibrido/plantillas`              → `h1` = «Plantillas»  (control)
          · `#/brazos/hibrido/plantillas/botones`      → «Esta pantalla no existe…»
          · `#/brazos/hibrido/plantillas/botones/nueva`→ «Esta pantalla no existe…»

        ⚠️ **Y las dos fallaban por causas DISTINTAS**, que es lo que hacía que
        arreglar un solo lado dejara la otra en rojo sin explicación:

          · `plantillas/botones` **sí** estaba en `PANTALLAS_DEL_BRAZO`, así que
            `pantallaDesconocida` daba `false` y estas rutas del brazo SÍ se
            montaban… y ninguna hermana matcheaba ⇒ ganaba el `<Route path="*">`
            del marco, unos renglones más abajo;
          · `plantillas/botones/nueva` **no** estaba en la lista, así que lo apagaba
            `pantallaDesconocida` y no llegaba ni a esta tabla.

        ⛔⛔ **SON DOS `<Route>` HERMANAS, Y NO UN `path="botones/*"`.** El motivo es
        el MISMO que el bloque de `agregarPlantilla` de arriba ya dejó escrito y que
        `rutas.ts` §`PANTALLAS_DEL_BRAZO` argumenta entero: esa lista es **cerrada**
        y se compara literal. Un comodín acá aceptaría
        `…/plantillas/botones/cualquier-cosa` montando algo mientras la lista dice
        que esa dirección no existe — dos fuentes diciendo cosas distintas sobre la
        misma dirección, que es exactamente lo que `tsc` no puede sostener.

        ⭐ **El control negativo que lo impide está escrito**, y sin él un comodín
        pasaría los otros tres casos igual: `pruebas/r2-ruta-de-botones.test.tsx`
        exige que `…/botones/cualquier-cosa` SIGA dando «Esta pantalla no existe».

        ⚠️ **El `element=` es un PROVISORIO con dueño** — ver
        `PantallaDeBotonesPendiente`. La pantalla es de `R2-UI8`
        (`src/plantillas/**`), que es territorio de otra etapa.
      */}
      {/*
        ⛔⛔⛔ **RASTRO · 23/9/2026, `R2-UI8` (`ADR-0348`) · ACÁ DECÍA
        ~~`element={<PantallaDeBotonesPendiente />}`~~ EN LAS DOS.**

        Era el provisorio que `ui-marco` dejó con dueño —*«este bloque se BORRA
        cuando la pantalla aterrice, y lo que queda es el `element=` de las dos
        `<Route>` de abajo apuntando a `src/plantillas/**`»*—. La pantalla
        aterrizó: es la pestaña ② de `PantallaPlantillas`, que monta
        `src/plantillas/botones/pantalla.tsx`.

        ⭐ **Es el «rebase de una línea» que `SEGMENTOS.botonesDePlantillas`
        prometía**, y `PantallaDeBotonesPendiente` se va con él: dejarlo sin usos
        pondría rojo a `noUnusedLocals` y, peor, dejaría en el árbol una pantalla
        de «no está construida» que ya no describe nada.

        ⚠️ **`pruebas/r2-ruta-de-botones.test.tsx` sigue VERDE y tiene que
        seguirlo**: sus casos afirman **la dirección** —que no da «Esta pantalla no
        existe» y que el comodín SIGUE sin existir—, no qué se ve, exactamente para
        que este cambio de `element=` no los rompiera. Su propio encabezado lo
        declara.

        ⛔⛔ **SIGUEN SIENDO DOS `<Route>` HERMANAS**: el argumento del comodín de
        arriba no lo deroga esta etapa, y su control negativo sigue escrito.
      */}
      <Route
        path={SEGMENTOS.botonesDePlantillas}
        element={<PantallaPlantillas borde={bordePlantillas} pestana="botones" />}
      />
      <Route
        path={SEGMENTOS.nuevoBotonDePlantillas}
        element={<PantallaPlantillas borde={bordePlantillas} pestana="botones" panelNuevoBoton />}
      />
      {/* `AW11` · la pantalla «Exportación»: selector de lote(s) e historial. */}
      {/*
        ⭐ `R2-UI17` · `PantallaDelBrazo`: la más riesgosa de las cuatro — sin la
        llave, con el sello ya en el otro brazo, ofrecía los lotes del anterior.
      */}
      <Route
        path={SEGMENTOS.exportacion}
        element={
          <PantallaDelBrazo>
            <PantallaExportacion />
          </PantallaDelBrazo>
        }
      />
      {/* `AW12` · la pantalla «Métricas» de F4. `CP-F4-010` vive en `MetricasDelBrazo`. */}
      <Route path={SEGMENTOS.metricas} element={<MetricasDelBrazo />} />
      {/*
        ⭐⭐ `AW16` · el simulador de chat — **la única ruta condicionada de esta
        tabla**, y la condición es la compuerta ① de `ADR-0198`.

        Sin `botWebUrl` en `configuracion.json` **no hay `<Route>` y no hay
        renglón en el menú**: no se monta con un cartel de error, no se monta
        apuntando a `localhost`, y no se monta rota. Ver `SEGMENTOS.simulador` y
        `configuracion.ts` §`botWebUrl`, donde está el argumento entero y —lo que
        importa más— **la advertencia de que esta compuerta es COSMÉTICA**: las que
        protegen a `bot-web` son el flag de su entorno, su lista de orígenes CORS y
        su loopback. Ninguna vive acá.

        El borde va por PROP, como `bordePadron`, `bordeCarga` y `cargarElegibles`:
        el bloque ③ del encabezado ya dejó escrito que `gates/pantalla-envios.spec.ts`
        DESCUBRE los contextos barriendo `src/contexto-*.tsx`, así que un contexto
        nuevo cambia lo que ese arnés monta.
      */}
      {bordeSimulador !== null && (
        <Route path={SEGMENTOS.simulador} element={<SimuladorDelBrazo borde={bordeSimulador} bordePadron={bordePadron} bordeConversaciones={bordeConversaciones} bordeEnvios={bordeEnvios} bordePlantillas={bordePlantillas} />} />
      )}
    </>
  )
}

/**
 * ⭐⭐⭐ `R1.1-F2` · **LA TABLA DE DIRECCIONES, MONTADA.**
 *
 * ── LO QUE ACÁ HABÍA, TEXTUAL (no se borra) ─────────────────────────────────
 *
 *   *«── Sin brazo declarado ── El comportamiento de antes de `ADR-0136`,
 *   intacto: el servidor resuelve el brazo de la sesión y la pantalla no
 *   opina.»* + *«── ⭐ Con el brazo en la RUTA (`ADR-0136` ②) ── El MISMO juego
 *   de pantallas, montado bajo un prefijo […] ⚠️ Un segmento suelto que no sea
 *   el de una pantalla —`#/loquesea`— cae acá y **se toma como un brazo**. No se
 *   puede distinguir de un brazo real, porque los nombres de brazo son
 *   `[CONFIG]` por instancia y esta app no los conoce.»*
 *
 * ⛔ **Las dos mitades quedan derogadas por el PRD de R1.1, cada una por su
 * línea:**
 *
 *   · «sin brazo declarado» ⇒ `PRD_R1.1.md:310`: las nueve direcciones viejas sin
 *     brazo **van a Inicio**. R1.1 no tiene un modo «sin brazo»: o estás en una
 *     zona, o estás eligiendo cuál (`R1.1-F2-CA-61`…`CA-69`);
 *   · «un segmento suelto se toma como un brazo» ⇒ `PRD_R1.1.md:204`
 *     («solo existen los brazos `hibrido` y `puro`; cualquier otro valor se
 *     rechaza con error») y `:324` (`#/brazos/<no válido>/…` muestra «Este brazo
 *     no existe en KAI»). El brazo pasa a ser lista cerrada: ver `zonas.ts`, que
 *     tiene el argumento entero y el límite de lo que deroga.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ POR QUÉ LA CANONICALIZACIÓN VA ANTES DE `<Routes>` Y NO ADENTRO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Los criterios de redirección dicen **«queda en»**, no «lleva a»: la barra de
 * direcciones tiene que terminar diciendo la dirección nueva. Un `<Route>` con
 * `<Navigate>` puede hacerlo, pero entonces el orden de veinte renglones decide
 * el resultado y ese orden no está escrito en ningún lado. Acá la decisión la
 * toma `canonicalizar()`, que es una función pura con sus reglas numeradas, y
 * `<Routes>` sólo ve direcciones **ya canónicas**.
 *
 * ⛔⛔ **Y hay un caso que SÓLO se puede resolver así**: `R1.1-F2-CA-96` pide que
 * `#/brazos//participantes` muestre «Este brazo no existe en KAI». Ese segmento
 * vacío del medio **no lo matchea ningún `path` de `react-router`** —`:brazo` no
 * captura la cadena vacía— así que por la tabla de rutas caería en el comodín y
 * terminaría en Inicio, que es un resultado distinto del que el criterio pide.
 * Acá el brazo se valida MIRANDO LOS SEGMENTOS, antes de que `react-router` los
 * toque, y los cinco casos de brazo inválido —`xyz`, mayúsculas, vacío, con
 * prefijo `brazos/` y sin él— salen por la misma puerta.
 */
function Rutas({
  conSimulador,
  pantallasDelBrazo,
  raizDeIdentidad,
  bordeSesion,
  lectorDelBrazoDelAcompanante,
}: {
  /** ⭐ `R2-UI19` · pide `GET /configuracion` a `admin-web` (`ADR-0384` E). */
  readonly lectorDelBrazoDelAcompanante: LectorDelBrazoDelAcompanante
  readonly conSimulador: boolean
  readonly pantallasDelBrazo: ReactNode
  /** La raíz de `auth.<sitio>`, para las dos pantallas de `src/login/`. */
  readonly raizDeIdentidad: string
  /** El borde ya armado, para que las pantallas de la puerta no armen otro. */
  readonly bordeSesion: BordeSesion
}): JSX.Element | null {
  const { pathname } = useLocation()
  const { estado, refrescar } = useSesion()
  /*
   * ⭐⭐ `R2-UI19` · **LA DECLARACIÓN SE PIDE ACÁ, UNA VEZ, EN CUANTO LA SESIÓN DICE
   * QUE EL ROL LA NECESITA** («inmediatamente después» de `quienSoy()`,
   * `r2-ui19.md` «Momento»). Antes de los `return` por fase: es un hook.
   * El Administrador no la pide (cae en Inicio).
   */
  const brazoDelAcompanante = useBrazoDelAcompanante(
    lectorDelBrazoDelAcompanante,
    estado.fase === "abierta" && necesitaElBrazoDelAcompanante(estado.rol),
  )
  const brazoResuelto = brazoDelAcompanante.fase === "resuelto" ? brazoDelAcompanante.brazo : null
  /**
   * ⭐ `R2-UI6` · el adaptador entre `src/login/` y `rutas.ts`: `rol: string` del
   * cable ⇒ `Role`, con el `#` puesto. Un rol que esta consola no conoce vuelve a la
   * puerta.
   *
   * ⟲ RASTRO `[25/9/2026]` · `R2-UI19`: era una función de módulo sobre
   * `destinoPorRol(rol)`. Ahora el destino del Acompañante depende de la declaración,
   * que en la puerta todavía no se pidió; en ese caso devuelve Inicio, que es desde
   * donde la guardia decide igual (`navegar` no navega: hace `refrescar()`, y la
   * dirección la pone `canonicalizar`). Nunca un brazo adivinado.
   */
  const destinoDelRolCrudo = (rolCrudo: string): string => {
    const rol = rolDeLaSesion(rolCrudo)
    return `#${rol === null ? RUTA_LOGIN : (destinoPorRol(rol, brazoResuelto) ?? INICIO)}`
  }

  /*
   * ════════════════════════════════════════════════════════════════════════
   * ⭐⭐⭐ `R2-UI6` · **ORDEN 0 — LA GUARDIA DE SESIÓN, ARRIBA DE `<Routes>` Y
   *       **ANTES** DE `canonicalizar()`.** `// ADR-0353`
   * ════════════════════════════════════════════════════════════════════════
   *
   * PRD:2198-2207 numera cuatro desenlaces para «acá no hay nada» y el **0 es la
   * sesión**: 0 sin sesión ⇒ login · 1 «Este brazo no existe» · 2 «Esta pantalla
   * no existe» · 3 «No tienes acceso». Este bloque es el 0, y los tres de abajo
   * están en ese orden, en este archivo, a la vista.
   *
   * ⛔⛔ **Y NO ES UNA CUESTIÓN DE GUSTO DÓNDE VA: `canonicalizar` DESTRUYE LA
   * DIRECCIÓN DEL LOGIN.** Su regla ⑧ (`rutas.ts`: «cualquier otra cosa →
   * Inicio», `CA-89`) no conoce `#/login`, así que **reescribe `#/login` a `#/`
   * antes de que `<Routes>` lo vea**. Lo que hace un `react-router` de manual
   * —poner `<Route path="login">` adentro de `<Routes>` y no tocar nada más— deja
   * el orden 0 imposible de cumplir, y el síntoma es un rebote infinito entre
   * `#/` y `#/login` que ningún `tsc` delata. `F6.md` §10 riesgo 12.
   *
   * ⛔ **Las dos pantallas de la puerta se devuelven ACÁ, no por `<Route>`**: son
   * las únicas de la consola **sin marco**, y declararlas abajo obligaría a que
   * `canonicalizar` las conociera — o sea a meter la sesión adentro de la tabla de
   * direcciones de R1.1, que es donde no va.
   */

  /*
   * ⚠️⚠️ **`consultando` NO ES `sin-sesion`, Y POR ESO ACÁ NO SE DIBUJA NADA.**
   * Mientras `quienSoy()` viaja, la SPA no sabe quién es. Mandar al login en ese
   * hueco reescribe la barra de alguien que sí tiene sesión: `CA-F6-53` (la sesión
   * sobrevive a recargar) fallaría **de manera intermitente**, sólo cuando la red
   * tarda, que es el peor defecto que se puede dejar.
   *
   * ⚠️ `null` y no un cartel «Cargando…»: ningún criterio del PRD lo pide
   * (`ADR-0319`) y es un parpadeo de un turno de microtareas. **Es elección mía**
   * y se revierte con una línea.
   */
  if (estado.fase === "consultando") return null

  if (estado.fase === "sin-sesion") {
    /*
     * `CA-F6-01`, `CA-F6-02`, `CA-F6-52`: **cualquier** dirección sin sesión deja
     * la barra en `#/login`. El `replace` es por lo mismo que abajo: con un
     * `push`, «Atrás» rebotaría contra la dirección protegida y volvería acá.
     */
    if (pathname !== RUTA_LOGIN) return <Navigate to={RUTA_LOGIN} replace />
    return (
      <PantallaLogin
        raiz={raizDeIdentidad}
        borde={bordeSesion}
        destinoPorRol={destinoDelRolCrudo}
        /*
         * ⛔⛔ **`navegar` NO navega: vuelve a preguntar quién soy.** `P9`: *«la
         * guardia de la SPA se apoya en quién soy»*. Si acá se hiciera
         * `navigate(destino)`, la guardia de arriba seguiría en `sin-sesion` y
         * rebotaría al login — el operador entraría bien y volvería a la puerta.
         * Con `refrescar()`, el estado pasa a `abierta` y **la dirección la
         * decide `canonicalizar` desde `#/login`**, que por la regla ⑧ cae en
         * Inicio y el mapeo de rol lo lleva a donde corresponde. Un solo camino.
         */
        navegar={() => refrescar()}
      />
    )
  }

  if (estado.fase === "debe-elegir") {
    /*
     * Regla 6 (PRD:2304) y `CA-F6-16`: mientras la marca está puesta, la ÚNICA
     * dirección es `#/elegir-contrasena`. Se decide con la respuesta de
     * `quienSoy()` y **no interceptando el 409** (P9).
     */
    if (pathname !== RUTA_ELEGIR_CONTRASENA) return <Navigate to={RUTA_ELEGIR_CONTRASENA} replace />
    return (
      <PantallaElegirContrasena
        raiz={raizDeIdentidad}
        borde={bordeSesion}
        destinoPorRol={destinoDelRolCrudo}
        navegar={() => refrescar()}
      />
    )
  }

  const rol = estado.rol

  /*
   * ⭐⭐ `R2-UI19` · **ANTES DE `canonicalizar()`: ¿ESTE ROL TIENE A DÓNDE CAER?**
   * `// ADR-0384 E`. Mientras la declaración viaja, nada (como `consultando`): ni
   * Inicio ni un brazo decidido sin datos. Resuelta sin brazo —cero o dos con
   * derivación, o la declaración no se pudo leer—, acceso denegado con texto
   * constante. ⛔ Nunca un brazo de repuesto.
   */
  if (necesitaElBrazoDelAcompanante(rol)) {
    if (brazoDelAcompanante.fase !== "resuelto") return null
    if (brazoDelAcompanante.brazo === null) return <SinBrazoDelAcompanante />
  }
  const destinoDelRol = destinoPorRol(rol, brazoResuelto) ?? INICIO

  /*
   * ⭐ Con sesión abierta, `#/login` y `#/elegir-contrasena` **no necesitan un
   * caso**: son direcciones que la tabla de R1.1 no conoce, así que caen en la
   * regla ⑧ y `canonicalizar` las manda al inicio del rol. Es exactamente lo que
   * tiene que pasar después de entrar.
   */
  const destino = canonicalizar(pathname, rol, brazoResuelto)

  /*
   * ⛔ `replace` y no un `push`: `R1.1-F2-CA-85` pide que tras pegar
   * `#/puro/metricas` desde Inicio, «Atrás» vuelva a `#/`. Con un `push` el botón
   * de atrás rebotaría contra la dirección vieja, que redirigiría de nuevo — el
   * operador quedaría atrapado.
   */
  if (destino !== null) return <Navigate to={destino} replace />

  /*
   * ⭐ El brazo, validado sobre los SEGMENTOS y no sobre un parámetro de ruta.
   * Ver el encabezado: es lo único que alcanza a `#/brazos//participantes`.
   */
  const partes = segmentosDe(pathname)
  if (partes[0] === SEGMENTOS.brazos && !esBrazo(partes[1])) return <BrazoInexistente />

  /*
   * ⛔⛔ **LA PANTALLA SE DECIDE ACÁ Y NO EN `<Routes>`, PORQUE `react-router` NO
   * PUEDE DECIDIRLA** — su `matchPath` es **INSENSIBLE A MAYÚSCULAS**.
   *
   * Medido con una sonda sobre `<App />` entero, el 17/9/2026:
   * `#/brazos/hibrido/Participantes` **montaba `section.padron`**, con el sello
   * puesto y sin ningún síntoma. `R1.1-F2-CA-102` (`PRD_R1.1.md:439`) pide que
   * muestre «Esta pantalla no existe en KAI · Brazo Híbrido».
   *
   * ⚠️⚠️ **Y EL `<Route path="*">` DE ADENTRO DEL MARCO NO ALCANZABA**, que es lo
   * que vuelve falso al arreglo intuitivo: el comodín sólo corre cuando NINGUNA
   * hermana matchea, y `participantes` sí matchea `Participantes`. Medido
   * apagando esta guarda: `CA-103` y `CA-104` —las dos pantallas de maqueta—
   * **siguen verdes**, porque ésas sí caen en el comodín; los que se ponen rojos
   * son exactamente los dos de mayúsculas. Las dos piezas cubren cosas distintas
   * y por eso están las dos.
   *
   * ⭐ Misma forma que la guarda del brazo, y por el mismo motivo: el producto
   * compara literal y el matcher del framework normaliza. Las dos decisiones
   * quedan juntas, arriba de `<Routes>`, donde se leen de un vistazo.
   */
  const colaDelBrazo =
    partes[0] === SEGMENTOS.brazos && esBrazo(partes[1]) ? partes.slice(2).join("/") : null
  const pantallaDesconocida =
    colaDelBrazo !== null && colaDelBrazo !== "" && !esPantallaDelBrazo(colaDelBrazo)

  /*
   * ⭐⭐ `R2-UI6` · **ORDEN 3 — «No tienes acceso a esta pantalla».** `// ADR-0353`
   * Regla 18 (PRD:2316), `CA-F6-35`, `CA-F6-37`, `CA-F6-38`, `CA-F6-41`.
   *
   * ⛔⛔ **VA ÚLTIMO, Y ESO ES UN CRITERIO, NO UN DETALLE DE ORDEN.**
   * `CA-F6-39`: *«“No existe” gana sobre “no tienes acceso”»* —
   * `#/brazos/hibrido/pantalla-inventada` con el Acompañante dice «Esta pantalla
   * no existe», no «no tienes acceso»—; `CA-F6-40`: *«“Este brazo no existe” gana
   * sobre todo lo demás»*. El `!pantallaDesconocida` de acá abajo y el `return
   * <BrazoInexistente/>` de más arriba son esos dos criterios escritos.
   *
   * ⛔ **Y para el Administrador es SIEMPRE `false`** (`esZonaDelRol` devuelve
   * `true` para quien tiene Inicio): `CA-F6-41` pide que el Administrador **nunca**
   * vea esta pantalla, y eso no depende de enumerar sus direcciones — depende de
   * que la pregunta se le haga a `destinoPorRol`.
   */
  const fueraDeZona = !pantallaDesconocida && !esZonaDelRol(pathname, rol, brazoResuelto)

  /*
   * ⭐ **Si un rol recibe las pantallas del brazo lo decide su MENÚ: ¿le da algún
   * renglón?** Con CERO renglones no se le monta ninguna `<Route>`.
   *
   * ⟦rastro · ADR-0082 · 5/10/2026⟧ · `R4-UIB1` (`CA-02`). Acá decía:
   * ~~«las pantallas del brazo que un rol tiene son EXACTAMENTE las que su menú
   * declara… de lo contrario el Acompañante no vería el renglón “Participantes”
   * pero podría llegar escribiendo la dirección, que es justo lo que CA-F6-35
   * prohíbe»~~. Eso ya NO describe el mecanismo. Al darle al Acompañante su
   * renglón (Situaciones críticas), `renglones.length > 0` es `true` para él y se
   * le monta el set COMPLETO de `pantallasDelBrazo` (`:2460-2462`), **no** sólo su
   * renglón. Lo que hoy le impide llegar a Participantes escribiendo la dirección
   * es `fueraDeZona` (`esZonaDelRol`, `:2410`), que hace que el comodín dibuje
   * «No tienes acceso» (`:2483-2485`) — no que la `<Route>` falte. El menú decide
   * si HAY montaje; la zona decide el ACCESO.
   *
   * ⟦rastro · ADR-0082 · 5/10/2026⟧ · El segundo motivo que había acá también
   * caducó: ~~«`pantallas()` abre con `<Route index … to="participantes">` y
   * montado para el Acompañante lo sacaría de `#/brazos/hibrido`»~~. Para el
   * Acompañante `canonicalizar` manda `#/brazos/<brazo>` a `…/situaciones-criticas`
   * (`rutas.ts:826-832`) antes de llegar a ese índice, así que nunca se alcanza.
   */
  const rolConPantallasDelBrazo = menuDelBrazo(rol, conSimulador).renglones.length > 0

  return (
    <Routes>
      <Route path={INICIO} element={<Inicio />} />
      {/*
        ⭐ El marco del brazo. El `:brazo` del `path` es lo que hace que
        `useBrazo()` —el ÚNICO lector del brazo, `./brazo.ts`— siga contestando
        sin cambiar: `padron/tabla.tsx`, `MetricasDelBrazo` y `useCargarPlantillas`
        no se enteran de que la tabla de rutas cambió de forma.

        ⚠️ Acá el brazo ya está validado por la guarda de arriba, así que
        `MarcoDelBrazo` lo recibe tipado como `Brazo` y no vuelve a decidir.
      */}
      <Route
        path={`${SEGMENTOS.brazos}/${SEGMENTO_BRAZO}`}
        element={<MarcoDelBrazoDeLaRuta conSimulador={conSimulador} />}
      >
        {/*
          ⭐ Con la pantalla desconocida, el juego de rutas **no se monta**: así el
          `<Route path="*">` de abajo es el único que puede matchear y el cartel
          sale DENTRO del marco, que es lo que `CA-100` pide (el sello sigue
          estando). ⛔ Devolver `<PantallaInexistente />` desde arriba de `<Routes>`
          —el arreglo de una línea— lo dibujaría SIN marco, y entonces `CA-100`
          quedaría rojo por arreglar `CA-102`.
        */}
        {pantallaDesconocida || fueraDeZona || !rolConPantallasDelBrazo
          ? null
          : pantallasDelBrazo}
        {/*
          ⟦rastro · ADR-0082 · 5/10/2026⟧ Se elimina el `<Route index>` que montaba
          `PantallaDelAcompanante` (fila 13 / B28). Al darle un renglón propio al
          Acompañante, `rolConPantallasDelBrazo` (arriba) es `true` para él y se le
          monta el set COMPLETO de `pantallasDelBrazo`, así que esta rama quedaba
          muerta; y `#/brazos/<brazo>` para el Acompañante lo resuelve
          `canonicalizar` (`rutas.ts:826-832`), que redirige al marco de
          Situaciones antes de que se alcance ningún índice del brazo.
        */}
        {/*
          ⛔ `R1.1-F2-CA-100`…`CA-105` · una pantalla que no existe se informa
          DENTRO del marco, con el sello puesto. No cae en el comodín global: el
          brazo existe, y mandar al operador a Inicio le haría perder la zona.

          ⭐ `R2-UI6` · **y la pantalla que existe pero el rol no alcanza, también**
          (Regla 18, `CA-F6-35`). Es el MISMO comodín porque es el mismo requisito
          —dibujar en la dirección que la persona escribió, dentro del marco— y
          cuál de los dos carteles sale ya se decidió arriba, donde los cuatro
          órdenes del PRD se leen juntos.
        */}
        <Route
          path="*"
          element={fueraDeZona ? <AccesoDenegado destino={destinoDelRol} /> : <PantallaInexistenteDeLaRuta />}
        />
      </Route>
      {/* ── Las transversales, con marco neutro. `R1.1-F3`. ── */}
      <Route path={`${SEGMENTOS.instancia}/${SEGMENTOS.bancoDeContenido}`} element={<MarcoTransversal />}>
        {/*
          ⭐ `R2-UI6` · `CA-F6-38` («el Acompañante no entra a las transversales»)
          contra `CA-F6-41` («el Administrador nunca ve esta pantalla»). El mismo
          `index`, con el cartel que el rol determine.
        */}
        <Route
          index
          element={fueraDeZona ? <AccesoDenegado destino={destinoDelRol} /> : <TablaBancoDeContenido />}
        />
      </Route>
      {/*
        ⛔ El `/*` es un REQUISITO CRUZADO que `configuracion-del-chatbot/rutas.ts`
        dejó pedido por escrito: *«el marco tiene que servir
        `instancia/configuracion-del-chatbot/*`, no sólo el prefijo exacto. Con la
        ruta de hoy —coincidencia EXACTA— las cinco direcciones no las sirve
        nadie, y esta pantalla quedaría sin montar en cuatro de las cinco. Ningún
        examen de esa stage puede ver eso: se ve recién al navegar.»*
      */}
      <Route
        path={`${SEGMENTOS.instancia}/${SEGMENTOS.configuracionDelChatbot}/*`}
        element={<MarcoTransversal />}
      >
        {/* ⭐ `R2-UI6` · lo mismo que el banco: `CA-F6-38` / `CA-F6-41`. */}
        <Route
          index
          element={
            fueraDeZona ? <AccesoDenegado destino={destinoDelRol} /> : <PantallaConfiguracionDelChatbot />
          }
        />
        <Route
          path="*"
          element={
            fueraDeZona ? <AccesoDenegado destino={destinoDelRol} /> : <PantallaConfiguracionDelChatbot />
          }
        />
      </Route>
      {/*
        ⚠️ El comodín es una RED y no una regla: `canonicalizar()` ya mandó a
        Inicio todo lo que no reconoce (`CA-89`, `CA-90`). Lo que llegue acá es
        una dirección canónica sin `<Route>`, o sea un defecto de esta tabla, y
        vuelve a Inicio en vez de dejar la pantalla en blanco.
      */}
      <Route path="*" element={<Navigate to={INICIO} replace />} />
    </Routes>
  )
}

/**
 * El marco del brazo, con el brazo leído de la ruta.
 *
 * Envoltorio mínimo por lo mismo que `MetricasDelBrazo`: `useBrazo()` sólo
 * contesta adentro del `<Route>`. ⛔ El `as Brazo` no es una promesa de este
 * componente: es la guarda de `Rutas`, que ya rechazó todo lo que no sea uno de
 * los dos. Si esa guarda se sacara, el marco dibujaría un sello con un nombre
 * inventado — por eso están juntos y por eso está escrito.
 */
function MarcoDelBrazoDeLaRuta({ conSimulador }: { readonly conSimulador: boolean }): JSX.Element {
  const brazo = useBrazo()
  const { estado } = useSesion()
  if (!esBrazo(brazo)) return <BrazoInexistente />
  /*
   * ⚠️ `Rutas` ya devolvió el login para cualquier fase que no sea `abierta`, así
   * que esto es inalcanzable **hoy**. Se escribe como una rama y no como un `as`
   * porque el que lo vuelve cierto es otro archivo: un `estado.rol!` acá sería una
   * promesa que `tsc` no puede sostener y que se rompe en silencio el día que
   * alguien monte este marco desde otro lado.
   */
  if (estado.fase !== "abierta") return <BrazoInexistente />
  return <MarcoDelBrazo brazo={brazo} conSimulador={conSimulador} rol={estado.rol} />
}

/** Lo mismo, para el cartel de pantalla inexistente. */
function PantallaInexistenteDeLaRuta(): JSX.Element {
  const brazo = useBrazo()
  if (!esBrazo(brazo)) return <BrazoInexistente />
  return <PantallaInexistente brazo={brazo} />
}

export function App({
  config,
  bordeSesion: bordeSesionInyectado,
}: {
  readonly config: ConfiguracionInstancia
  /**
   * ⭐ `R2-UI6` · **El borde de la sesión, SÓLO para el examen.** Misma costura
   * que `PropsPantallaLogin.borde` de `R2-UI5` y misma razón: sin esto, la única
   * forma de ejercer la guardia sería interceptar `fetch`, que es probar el
   * transporte. En producción no se pasa y se arma con `identityWebUrl`.
   *
   * ⛔ **Va por PROP y no por contexto**, como `bordePadron` y `cargarElegibles`:
   * `gates/pantalla-envios.spec.ts` descubre los contextos barriendo
   * `src/contexto-*.tsx`, y un contexto nuevo cambia lo que ese arnés monta.
   */
  readonly bordeSesion?: BordeSesion
}) {
  /*
   * ⛔⛔ `[22/9/2026]` · **ESTE COMPONENTE **NO** INSTALA LA DECLARACIÓN, Y ESO ES
   * UNA DECISIÓN, NO UN OLVIDO.**
   *
   * La instala `main.tsx`, **antes de montar** (`zonas.ts`
   * ⟦la-declaracion-se-instala-antes-de-montar⟧), porque `canonicalizar()` de
   * `rutas.ts` es una función pura que corre fuera de React y tiene que poder
   * leerla sin un contexto.
   *
   * ⚠️ Se probó ponerlo también acá —`instalarLaDeclaracion(config)` en el primer
   * renglón del render— y **fue peor, medido**: 211 exámenes en rojo de una vez.
   * El motivo es el que importa, así que queda escrito: los exámenes montan
   * `<App config={CONFIG} />` con un `CONFIG` mínimo —`adminWebUrl`, `refrescoMs`,
   * `pageSize`— que **no declara brazos**, porque hasta hoy no hacía falta.
   * Instalar eso **pisaba** la declaración del proceso y dejaba a la consola sin
   * un solo brazo… que es, exactamente, lo que el código nuevo debe hacer ante
   * una instancia que no declara brazos. **El código estaba bien; la declaración
   * de esos exámenes estaba incompleta.**
   *
   * ⇒ la declaración es del PROCESO y se instala una vez en su arranque
   * (`main.tsx` en producción, `pruebas/preload.ts` en los exámenes); `config` es
   * la plomería por render. En producción `main.tsx` le pasa **el mismo objeto** a
   * las dos cosas, así que no pueden divergir.
   */

  /**
   * El proveedor se arma con la configuración ya leída, y por eso se construye
   * en el render de la raíz y no en el import: la dirección de `admin-web` no se
   * conoce hasta que `configuracion.json` llegó. `main.tsx` monta esto una sola
   * vez, después de resolverla.
   */
  const dataProvider = useMemo(() => crearProveedorDatos(config), [config])

  /**
   * `AW2` · `AW3` — el borde de ESCRITURA, armado acá y no adentro de cada
   * componente.
   *
   * Es la única razón por la que la pantalla se puede ejercer contra
   * `contracts/admin-api/fake.ts` «sin base, sin red y sin el módulo real»: el
   * borde entra por un contexto, así que una prueba monta la misma tabla con un
   * doble en lugar de éste. Construido adentro del modal, la única forma de
   * probarlo sería interceptar `fetch`, que es probar el transporte.
   *
   * **`useMemo` y no una construcción por render, y no es micro-optimización:**
   * el modal pide `uploadConstraints()` en un efecto que depende del borde. Un
   * borde nuevo en cada render haría que ese efecto se vuelva a disparar cada
   * vez, y como el efecto termina en un `setState`, el resultado es un lazo de
   * consultas contra `admin-web` que sólo se nota cuando el modal está abierto.
   * `dataProvider` queda memoizado por lo mismo.
   */
  const bordeDeEscritura = useMemo(() => crearBordeDeEscrituraHttp(config), [config])

  /**
   * `AW7` — el borde de ESCRITURA de participantes, armado acá igual que el de
   * documentos: la dirección de `admin-web` llega en tiempo de ejecución y el
   * borde entra por contexto para que la pantalla se pueda ejercer contra un
   * doble en las pruebas. Memoizado por la misma razón que `bordeDeEscritura`.
   */
  const bordeParticipantes = useMemo(() => crearBordeParticipantesHttp(config), [config])

  /**
   * `AW4` — el borde de LECTURA de conversaciones, armado acá igual que los otros
   * bordes: la dirección de `admin-web` llega en tiempo de ejecución y el borde
   * entra por contexto para que la pantalla se pueda ejercer contra un doble.
   * Memoizado por la misma razón que `bordeDeEscritura`.
   */
  const bordeConversaciones = useMemo(() => crearBordeConversacionesHttp(config), [config])

  /**
   * ⭐ `R4-UIB1` · el borde de Situaciones críticas, armado acá con el molde de los
   * otros bordes. `R4-UISC1` dejó el TIPO `BordeDeSituaciones` y la pantalla, pero
   * ningún productor real; esta etapa lo entrega. Memoizado por `config` por la
   * misma razón que `bordeDeEscritura`: un borde nuevo por render volvería a
   * disparar el efecto de carga de la pantalla.
   */
  const bordeSituaciones = useMemo(() => crearBordeDeSituacionesHttp(config), [config])

  /**
   * `AW10` — el borde de envíos, armado acá igual que los otros bordes: la
   * dirección de `admin-web` llega en tiempo de ejecución y el borde entra por
   * contexto para que la pantalla se pueda ejercer contra un doble. Memoizado por
   * la misma razón que `bordeDeEscritura`.
   */
  const bordeEnvios = useMemo(() => crearBordeEnviosHttp(config), [config])

  /**
   * `AW14` — la fuente de la audiencia elegible de «Nuevo envío» (`ADR-0090` ⑥).
   *
   * ⭐ Va por PROP y no por contexto, y es la única de las seis que lo hace: la
   * costura ya estaba puesta —`PantallaEnvios` acepta `cargarElegibles` y sin ella
   * cae a `SIN_FUENTE_DE_AUDIENCIA`, la fuente vacía—, así que lo que faltaba era
   * exactamente esto y nada más. Un contexto nuevo habría que descubrirlo, y
   * `gates/pantalla-envios.spec.ts` DESCUBRE los contextos barriendo
   * `src/contexto-*.tsx`: agregar uno cambia lo que ese arnés monta. Por la prop
   * no se toca nada de eso, y el default vacío sigue sirviendo a los exámenes que
   * montan la pantalla sin fuente.
   *
   * **`useMemo` y acá NO es micro-optimización, es correctitud:** `cargarElegibles`
   * es una dependencia del `useEffect` que carga la tabla de selección. Una
   * función nueva por render volvería a pedir la página en cada render — un bucle
   * de fetch contra el padrón de ~16.000 personas.
   */
  const cargarElegibles = useMemo(() => crearCargarElegiblesHttp(config), [config])

  /**
   * `AW11` — el borde de exportación, armado acá igual que los otros bordes: la
   * dirección de `admin-web` llega en tiempo de ejecución y el borde entra por
   * contexto para que la pantalla se pueda ejercer contra un doble. Memoizado
   * por la misma razón que `bordeDeEscritura`.
   */
  const bordeExportacion = useMemo(() => crearBordeExportacionHttp(config), [config])

  /**
   * `AW12` — el borde de métricas, armado acá igual que los otros bordes: la
   * dirección de `admin-web` llega en tiempo de ejecución y el borde entra por
   * contexto para que la pantalla se pueda ejercer contra un doble. Memoizado
   * por la misma razón que `bordeDeEscritura`.
   */
  const bordeMetricas = useMemo(() => crearBordeMetricasHttp(config), [config])

  /**
   * `AW15` — el borde de LECTURA del padrón, armado acá igual que los otros.
   *
   * ⭐ **Va por PROP y no por contexto**, como `cargarElegibles`: este archivo ya
   * dejó escrito (bloque ③) que `gates/pantalla-envios.spec.ts` DESCUBRE los
   * contextos barriendo `src/contexto-*.tsx`, así que un contexto nuevo cambia
   * lo que ese arnés monta. Por la prop no se toca nada de eso.
   *
   * ⚠️ **La segunda dependencia es el listado de LOTES, y es un hueco de la
   * cadena que se declara acá en vez de taparse.** `AP28` sirve el padrón pero
   * no agrega ninguna ruta que liste los lotes del brazo; la única que existe es
   * `GET exportaciones/lotes`, que `bordeExportacion` ya consume, y del otro
   * lado del cable es la MISMA fuente —`contracts/exports-api` importa el
   * listado del selector de `contracts/participants`, `LotesDisponibles =
   * Pick<ParticipantsRead, "lotesDe">`, justamente para que no haya «una segunda
   * declaración» de qué lotes tiene un brazo—. Se REUSA la función que existe en
   * vez de escribir un segundo `fetch` con un segundo literal de esa ruta, que
   * es el modo de falla que `borde-participantes.ts` ya dejó escrito: el día que
   * la ruta cambie, uno se cae y el otro le sigue hablando a un endpoint que no
   * existe, con `tsc` en cero. **Que el padrón dependa de la ruta de exportación
   * para su selector va reportado, con dueño `apps-integrator`.**
   *
   * `useMemo` y acá NO es micro-optimización, es correctitud: el borde es
   * dependencia de los dos `useEffect` de la tabla, y uno nuevo por render sería
   * un bucle de consultas contra el padrón de ~16.000 personas.
   */
  const bordePadron = useMemo(
    () => crearBordePadronHttp(config, bordeExportacion.lotes),
    [config, bordeExportacion],
  )

  /**
   * `AW17` — el borde de la CARGA EN LOTE, armado acá igual que los otros.
   *
   * ⭐ **Va por PROP y no por contexto**, como `bordePadron` y `cargarElegibles`,
   * por el motivo del bloque ③: `gates/pantalla-envios.spec.ts` DESCUBRE los
   * contextos barriendo `src/contexto-*.tsx`, así que un contexto nuevo cambia lo
   * que ese arnés monta.
   *
   * ⭐ `useMemo` y acá **NO es micro-optimización, es correctitud**: el borde es
   * dependencia del `useEffect` que vuelve a preguntar por el reporte, y uno
   * nuevo por render reiniciaría ese bucle en cada render — un martilleo contra
   * `admin-web` por una carga que todavía se está procesando.
   */
  const bordeCarga = useMemo(() => crearBordeCargaHttp(config), [config])

  /**
   * ⭐⭐ `AW16` — el borde contra **`apps/bot-web`**, o `null`.
   *
   * ⛔ **Es el primer borde de esta aplicación que no le habla a `admin-web`**, y
   * eso enmienda un invariante escrito en cuatro sitios vivos: `ADR-0197`
   * (`accepted`, 26/8/2026), *«`apps/management-ui` le habla DIRECTO a
   * `apps/bot-web`, por HTTP, con CORS de orígenes explícitos y nunca `*`»*. El
   * motivo es de perímetro y no de comodidad: un proxy por `admin-web` **juntaría
   * el plano HUMANO y el plano PARTICIPANTE en el proceso que tiene las
   * credenciales**. Los dos sitios del invariante que son territorio de esta etapa
   * —`README.md` y `configuracion.ts`— quedan redactados; los otros dos
   * (`specs/F1-…/pantalla.md:111` y `arquitectura.html:139`) **no lo son y quedan
   * reportados con dueño TL**.
   *
   * ⭐ **`null` es la compuerta, y un solo valor decide las dos cosas** —si la
   * ruta existe y si el renglón del menú aparece—, de modo que no puedan divergir.
   *
   * `useMemo` y acá **no es micro-optimización, es correctitud**: el borde es
   * dependencia del `useEffect` que sondea `GET /entregas`, y uno nuevo por render
   * reiniciaría ese sondeo en cada render — un martilleo contra un proceso que no
   * autentica a nadie.
   */
  const bordeSimulador = useMemo(() => bordeSimuladorDeLaInstancia(config), [config])

  /**
   * ⭐⭐ `AW46` — el borde del CATÁLOGO DE PLANTILLAS, armado acá igual que los
   * otros: la dirección de `admin-web` llega en tiempo de ejecución y el borde
   * entra por parámetro para que las pantallas se puedan ejercer contra un doble.
   *
   * ⛔ **Le habla a `admin-web`, no a Meta** (`AW46` §`guard` ②): `management-ui`
   * es un artefacto estático en S3 + CloudFront, así que una llamada a la Graph
   * API desde acá exigiría que la credencial WABA del brazo viajara al navegador
   * de quien abre la pantalla.
   *
   * `useMemo` y acá **no es micro-optimización, es correctitud**: el borde es
   * dependencia del `useEffect` que lee el catálogo en tres pantallas, y uno nuevo
   * por render sería un lazo de consultas contra `admin-web` — y, peor, la puerta
   * abierta a que el disparo de sincronización termine en un efecto, que es lo que
   * el contrato pide expresamente que no pase.
   */
  const bordePlantillas = useMemo(() => crearBordePlantillasHttp(config), [config])

  /**
   * ⭐ `AW48` · el borde de la configuración del chatbot.
   *
   * `useMemo` y acá **no es micro-optimización, es correctitud**: la pantalla lo
   * tiene como dependencia de su `useEffect` de carga, así que un borde nuevo por
   * render volvería a pedir la configuración entera en un bucle.
   *
   * ⛔ **No lleva brazo, y es una decisión de `R1-F2 §5.1`**: los textos son de la
   * INSTANCIA. Es la divergencia deliberada con `bordePlantillas`, que sí lo
   * lleva; los dos criterios están escritos en sus bordes.
   */
  const bordeConfiguracion = useMemo(() => crearBordeConfiguracionHttp(config), [config])

  /**
   * ⭐⭐ `R2-UI6` · **EL BORDE DE LA SESIÓN.** `// ADR-0353`
   *
   * Es el único que **no** le habla a `admin-web`: le habla a `auth.<sitio>`
   * (`apps/identity-web`), y su raíz sale de `identityWebUrl` —campo obligatorio
   * del `[CONFIG]` de instancia, ver `configuracion.ts`—.
   *
   * `useMemo` y acá **es correctitud, no micro-optimización**: el borde es
   * dependencia del efecto que consulta `quienSoy()` **una sola vez** en
   * `ProveedorDeSesion`. Uno nuevo por render volvería a consultar en cada render
   * — un martilleo contra el servicio de identidad, y un estado de sesión que se
   * reinicia solo.
   */
  const bordeSesion = useMemo(
    () => bordeSesionInyectado ?? crearBordeSesion(config.identityWebUrl),
    [bordeSesionInyectado, config],
  )

  /** ⭐ `R2-UI19` · `GET /configuracion` de `admin-web`, para el brazo del Acompañante. */
  const lectorDelBrazoDelAcompanante = useMemo(
    () => crearLectorDelBrazoDelAcompanante(config.adminWebUrl),
    [config],
  )

  return (
    <HashRouter>
      {/*
        ⛔⛔ `R2-UI6` · **EL PROVEEDOR DE SESIÓN VA ADENTRO DEL `HashRouter` Y
        ARRIBA DE TODO LO DEMÁS.** `// ADR-0353`

        · **Adentro del router**, porque `Rutas` decide con `useLocation()` y
          navega con `<Navigate>`: afuera, la guardia no tendría dirección que
          mirar ni a dónde ir.
        · **Arriba de los bordes**, porque la reacción central a 401/403/409 se
          registra en `mensaje-de-error.ts` al montarse, y los trece bordes pasan
          por ahí. Montado más abajo, habría un intervalo en el que una pantalla
          ya puede pedir datos y nadie está escuchando el 401.
      */}
      <ProveedorDeSesion borde={bordeSesion}>
      <ProveedorConfiguracion value={config}>
        <ProveedorBordeEscritura value={bordeDeEscritura}>
          <ProveedorBordeParticipantes value={bordeParticipantes}>
            <ProveedorBordeConversaciones value={bordeConversaciones}>
              <ProveedorBordeEnvios value={bordeEnvios}>
                <ProveedorBordeExportacion value={bordeExportacion}>
                  <ProveedorBordeMetricas value={bordeMetricas}>
                <ProveedorBordeConfiguracion value={bordeConfiguracion}>
            <Refine
              dataProvider={dataProvider}
              /**
               * `ADR-0136` ①. El enrutador entra; la lista de `resources` NO se
               * toca — sigue teniendo el único recurso que el `dataProvider`
               * sirve. Es deliberado: `resources` es lo que Refine usa para
               * INFERIR el recurso de una ruta, y las seis pantallas nuevas no
               * pasan por el `dataProvider` (cada una tiene su propio borde
               * HTTP). Declararlas como recursos les daría a `useTable` y
               * compañía un recurso inferido que nadie sirve.
               *
               * `TablaBancoDeContenido` pasa `resource: RECURSO_DOCUMENTOS`
               * explícito, así que la inferencia por ruta no la toca a ella
               * tampoco.
               */
              routerProvider={routerProvider}
              resources={[
                {
                  name: RECURSO_DOCUMENTOS,
                  /*
                   * ⚠️ `R1.1-F2` · el banco se mudó a `#/instancia/banco-de-contenido`
                   * (`PRD_R1.1.md:468`). ~~`/${SEGMENTOS.documentos}`~~ ya no
                   * existe como dirección: quedaba apuntando a una ruta que el
                   * comodín se come, y `TablaBancoDeContenido` pasa su
                   * `resource` explícito igual, así que esto no lo usa nadie
                   * para navegar — pero decir una ruta que no existe es
                   * exactamente lo que este archivo dice que no se hace.
                   */
                  list: RUTA_DEL_BANCO,
                  meta: { label: "Banco de Contenido" },
                },
              ]}
              options={{
                /**
                 * El telemetry de Refine manda datos a un servidor de terceros.
                 * Se apaga: esta pantalla trabaja sobre el banco de contenido de
                 * un estudio con ~16.000 menores, y no hay ninguna razón por la
                 * que algo de acá tenga que salir hacia un destino que no sea
                 * `admin-web`.
                 */
                disableTelemetry: true,
                /**
                 * **Explícito ahora que hay enrutador, y en `false`.** Con
                 * `syncWithLocation` prendido, Refine escribe el paginado y el
                 * orden de `useTable` en el query string y los vuelve a leer de
                 * ahí. Sería un cambio de comportamiento de `AW1` que ningún
                 * caso pide, y la tabla del banco de contenido ya tiene su
                 * propio estado de orden y página examinado. Se deja apagado y
                 * escrito, para que quede claro que es una decisión y no el
                 * default que nadie miró.
                 */
                syncWithLocation: false,
                /**
                 * `warnWhenUnsavedChanges` sigue APAGADO, y ahora por una razón
                 * distinta de la de antes.
                 *
                 * Antes estaba apagado porque esa opción cuelga del enrutador de
                 * Refine —intercepta una NAVEGACIÓN con un formulario sucio— y no
                 * había enrutador: era una opción inerte que se leía como una
                 * protección. **Ahora hay enrutador, así que prenderla haría
                 * algo**, y sigue apagada porque prenderla exige montar el
                 * `<UnsavedChangesNotifier />` de `@refinedev/react-router` y
                 * porque lo que protegería —una navegación con el modal de carga
                 * abierto— no es el modo de falla que está abierto (ver abajo).
                 * Prenderla es un cambio de producto y no un detalle de
                 * cableado; queda anotada en el informe como no hecha.
                 *
                 * **Contra qué protege el modal, y contra qué NO** —porque lo
                 * segundo también es cierto y estaba faltando acá—:
                 *
                 *   · SÍ · el clic al costado. El diálogo no cierra al hacer clic
                 *     en el fondo (`dialogo.tsx`), que es el gesto descuidado con
                 *     el que se perdería una selección sin haberla querido perder.
                 *   · NO · **Escape**. `dialogo.tsx` cierra con Escape y
                 *     `tabla.tsx` desmonta el modal, así que un operador con diez
                 *     archivos ya elegidos que apriete Escape por reflejo los
                 *     pierde y tiene que volver a buscarlos en el disco.
                 *
                 * Escape se deja cerrando a propósito —es la convención de la
                 * plataforma y el único escape por teclado del diálogo, y en la
                 * confirmación de borrado es exactamente lo que uno quiere que
                 * haga—. Que en el modal de carga cueste una selección es un
                 * costo real y **no está resuelto**: queda anotado en el informe,
                 * no tapado acá.
                 */
                warnWhenUnsavedChanges: false,
              }}
            >
              <Rutas
                lectorDelBrazoDelAcompanante={lectorDelBrazoDelAcompanante}
                conSimulador={bordeSimulador !== null}
                raizDeIdentidad={config.identityWebUrl}
                bordeSesion={bordeSesion}
                pantallasDelBrazo={pantallas(
                  cargarElegibles,
                  bordePadron,
                  bordeCarga,
                  bordeSimulador,
                  bordeEnvios,
                  bordeConversaciones,
                  bordePlantillas,
                  bordeSituaciones,
                )}
              />
            </Refine>
                  </ProveedorBordeConfiguracion>
                  </ProveedorBordeMetricas>
                </ProveedorBordeExportacion>
              </ProveedorBordeEnvios>
            </ProveedorBordeConversaciones>
          </ProveedorBordeParticipantes>
        </ProveedorBordeEscritura>
      </ProveedorConfiguracion>
      </ProveedorDeSesion>
    </HashRouter>
  )
}
